import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputText, InputText as PrimeInputText } from "primereact/inputtext";
import { useNavigate, useParams } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import useToast from "../../components/common/useToast";
import apiFunctions from "../../services/api-service";
import { AxiosError } from "axios";
import { errorHandler, renderFormattedText } from "../../utils/HelperFunctions";
import "../../assets/css/datatable.css";
import BazarProduct from "../../models/bazarProduct";
import { Card } from "primereact/card";
import { Accordion, AccordionTab } from "primereact/accordion";
import TreeComponent from "./TreeComponent";
import { Carousel } from "primereact/carousel";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import 'react-circular-progressbar/dist/styles.css';
import { Paginator, PaginatorCurrentPageReportOptions, PaginatorRowsPerPageDropdownOptions } from "primereact/paginator";
import { Dropdown } from "primereact/dropdown";
import LocationMap, { LocationPoint } from "../../components/common/LocationMap";
import ImagePreviewModal from "../../components/common/ImagePreviewModal";
import CountryFlag from "../../components/common/CountryFlag";
import DateFormate from "../../libs/DateFormate";
import { PencilIcon } from "@heroicons/react/24/outline";
import {
    ArrowLeftIcon,
    ArrowRightIcon,
    CheckCircleIcon,
    XCircleIcon,
    TrashIcon,
    MagnifyingGlassIcon,
    ChatBubbleLeftRightIcon
} from "@heroicons/react/24/outline";
import { Skeleton } from "primereact/skeleton";
import { confirmDialog } from "primereact/confirmdialog";
import { persistor } from "../../redux/store";
import { logout } from "../../redux/features/authSlice";
import { useDispatch } from "react-redux";
import { InputTextarea } from "primereact/inputtextarea";
import { Dialog } from "primereact/dialog";
import { Calendar } from "primereact/calendar";
import { MultiSelect } from "primereact/multiselect";
import WhatsAppTemplateSection from "../../components/common/WhatsAppTemplateSection";
import WhatsAppIcon from "../../components/common/WhatsAppIcon";
import ChatMessageContent from "../../components/common/ChatMessageContent";
import { Select, Spin } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";

const { Option } = Select;

// Groups chat messages into date-separated sections, e.g. "MONDAY, JUN 15" —
// same grouping used by the standalone Chat module (ChatPage.tsx).
const dateLabel = (d: string) => {
    const date = new Date(d);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
    if (sameDay(date, today)) return "TODAY";
    if (sameDay(date, yesterday)) return "YESTERDAY";
    return date.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" }).toUpperCase();
};

const CHAT_TYPE_TABS: { value: string; label: string }[] = [
    { value: "", label: "All" },
    { value: "text", label: "Text" },
    { value: "media", label: "Media" },
    { value: "audio", label: "Audio" },
    { value: "files", label: "Files" },
];

// Accordion-style section: closed by default, and calls onOpen (used to
// trigger that section's API call) only the first time it's expanded —
// children are never mounted while closed. Visually a Card with a
// clickable header, same look as the Cards elsewhere on this page.
const CollapsibleSection: React.FC<{ title: string; defaultOpen?: boolean; onOpen?: () => void; children: React.ReactNode }> = ({ title, defaultOpen = false, onOpen, children }) => {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <div className="w-full mt-2" style={{ background: "#fff", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
            <button
                type="button"
                onClick={() => {
                    setOpen((prev) => {
                        const next = !prev;
                        if (next) onOpen?.();
                        return next;
                    });
                }}
                className="w-full flex items-center justify-between"
                style={{ padding: "1rem 1.25rem", background: "transparent", border: "none", cursor: "pointer", fontWeight: 600, fontSize: "1.1rem", color: "#264164" }}
            >
                <span>{title}</span>
                <span style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s" }}>▾</span>
            </button>
            {open && <div style={{ padding: "0 1.25rem 1.25rem" }}>{children}</div>}
        </div>
    );
};

const ProfileView: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("Name is required!"),
        namegujarati: Yup.string().required("Gujarati Name is required!"),
        fathername: Yup.string().required("Father Name is required!"),
        fathernamegujarati: Yup.string().required("Gujarati Father Name is required!"),
        surnameid: Yup.string().required("Surname is required!"),
        subcastid: Yup.string().required("Subcast is required!"),
        gender: Yup.string().required("Gender is required!"),

        dob: Yup.string().required("Date of Birth is required!"),

        doa: Yup.string().notRequired().nullable(),

        // isalive should be boolean
        isalive: Yup.boolean().required("Is Alive is required!"),

        // ✅ DOD depends on isalive
        dod: Yup.string().when("isalive", {
            is: false,
            then: (schema) => schema.required("Date of Death is required!"),
            otherwise: (schema) => schema.notRequired().nullable(),
        }),

        maritalstatus: Yup.string().required("Marital Status is required!"),

        mobileno: Yup.string().required("Mobile Number is required!"),

        profiletype: Yup.string().required("Profile Type is required!"),
    });

    const initialValue = {
        honorifics: "",
        name: "",
        namegujarati: "",
        fathername: "",
        fathernamegujarati: "",
        surnameid: "",
        subcastid: null,
        gender: "",
        dob: null,
        doa: null,
        isalive: true,
        dod: null,
        maritalstatus: "",
        bloodgroup: "",
        mobileno: "",
        bio: "",
        homeaddress: "",
        profiletype: "",
        nativeplaceid: "",
        nativelat: null as number | null,
        nativelng: null as number | null,
        nativeaddress: "",
        nativecity: "",
        currentplaceid: "",
        currentlat: null as number | null,
        currentlng: null as number | null,
        currentaddress: "",
        currentcity: "",
    };

    const formik = useFormik({
        initialValues: initialValue,
        validationSchema,
        enableReinitialize: true,
        validateOnBlur: false,
        validateOnChange: false,
        onSubmit: async (values) => {
            await apiFunctions.put(`/user/update-profile/${id}`, values);
            showToast({
                severity: "success",
                summary: "Success",
                detail: "Profile updated successfully",
            });
            setIsEditMode(false);
            getUserData();
        },
    });

    const dispatch = useDispatch();

    const [images, setImages] = useState<any[]>([]);
    const [postDescription, setPostDescription] = useState<string>("");
    const [postDate, setPostDate] = useState<any>(new Date());
    const [postLike, setPostLike] = useState<number>(0);
    const [postComment, setPostComment] = useState<number>(0);
    const [postLatestid, setPostLatestid] = useState<any>(null);
    const [postFirstid, setPostFirstid] = useState<any>(null);
    const [postPrevid, setPostPrevid] = useState<any>(null);
    const [postNextid, setPostNextid] = useState<any>(null);
    const [postCurrentid, setPostCurrentid] = useState<any>(null);

    const [followingData, setFollowingData] = useState<any[]>([]);
    const [dataLoad, setDataLoad] = useState(true);
    const [userdata, setUserdata] = useState<any>(null);
    const [load, setLoad] = useState(true);
    const initialized = useRef(false);
    const [itemsPerPage] = useState<any>(10);
    const { showToast }: any = useToast();
    const [name, setName] = useState<string>("");
    const [followersData, setFollowersData] = useState<any[]>([]);
    const [likeData, setLikeData] = useState<any[]>([]);
    const [commentData, setCommentData] = useState<any[]>([]);
    const [itemsFollowersPerPage, setItemsFollowersPerPage] = useState<any>(10);
    const [currentFollowersPage, setCurrentFollowersPage] = useState(0);
    const [skipFollowersData, setSkipFollowesData] = useState(0);
    const [totalFollowersRecords, settotalFollowersRecords] = useState(0);
    const [totalFollowingsPages, settotalFollowingsPages] = useState<any>(0);
    const [itemsFollowingsPerPage, setItemsFollowingsPerPage] = useState<any>(10);
    const [currentFollowingsPage, setCurrentFollowingsPage] = useState(0);
    const [skipFollowingsData, setSkipFollowingsData] = useState(0);
    const [totalFollowingRecords, settotalFollowingsRecords] = useState(0);
    const [totalFollowersPages, settotalFollowersPages] = useState<any>(0);
    const startFollowersIndex = (currentFollowersPage - 1) * itemsFollowersPerPage;
    const startFollowingsIndex = (currentFollowingsPage - 1) * itemsFollowingsPerPage;

    const [totalLikesPages, settotalLikesPages] = useState<any>(0);
    const [itemsLikesPerPage, setItemsLikesPerPage] = useState<any>(10);
    const [currentLikesPage, setCurrentLikesPage] = useState(0);
    const [skipLikesData, setSkipLikesData] = useState(0);
    const [, settotalLikesRecords] = useState(0);
    const startLikesIndex = (currentLikesPage - 1) * itemsLikesPerPage;

    const [totalCommentsPages, settotalCommentsPages] = useState<any>(0);
    const [itemsCommentsPerPage, setItemsCommentsPerPage] = useState<any>(10);
    const [currentCommentsPage, setCurrentCommentsPage] = useState(0);
    const [skipCommentsData, setSkipCommentsData] = useState(0);
    const [totalCommentRecords, settotalCommentsRecords] = useState(0);
    const startCommentsIndex = (currentCommentsPage - 1) * itemsCommentsPerPage;

    const [familyTreeData, setFamilyTreeData] = useState(null);

    // Family tree / followers / followings / post sections now load lazily
    // (see the Accordion below) — these track whether each one's already
    // been fetched once, so re-opening a tab doesn't refetch every time.
    const [familyTreeLoaded, setFamilyTreeLoaded] = useState(false);
    const [followersLoaded, setFollowersLoaded] = useState(false);
    const [followingsLoaded, setFollowingsLoaded] = useState(false);
    const [postLoaded, setPostLoaded] = useState(false);

    const [isEditMode, setIsEditMode] = useState(false);

    const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
    const nativePlaceInputRef = useRef<HTMLInputElement>(null);
    const currentPlaceInputRef = useRef<HTMLInputElement>(null);

    const [rows] = useState(10);

    const [surnameOption, setSurnameOption] = useState<any[]>([]);
    const [fetchingSurname, setFetchingSurname] = useState(false);
    const [firstSurname, setFirstSurname] = useState(0);
    const [surnameSearchValue, setSurnameSearchValue] = useState('');
    const [surnameTotalRecords, setSurnameTotalRecords] = useState(0);

    const [subcastOption, setSubcastOption] = useState<any[]>([]);
    const [fetchingSubcast, setFetchingSubcast] = useState(false);
    const [firstSubcast, setFirstSubcast] = useState(0);
    const [subcastSearchValue, setSubcastSearchValue] = useState('');
    const [subcastTotalRecords, setSubcastTotalRecords] = useState(0);

    const genderOptions = [
        { label: "Male", value: "Male" },
        { label: "Female", value: "Female" },
        { label: "Other", value: "Other" },
    ];

    const profileTypeOptions = [
        { label: "User", value: "User" },
        { label: "Subadmin", value: "Subadmin" }
    ]

    const maritalStatusOptions = [
        { label: "Single", value: "Single" },
        { label: "Married", value: "Married" },
        { label: "Divorced", value: "Divorced" },
        { label: "Widowed", value: "Widowed" },
    ];

    const bloodGroupOptions = [
        "A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-",
    ].map(bg => ({ label: bg, value: bg }));

    const responsiveOptions = [
        {
            breakpoint: '1400px',
            numVisible: 1,
            numScroll: 1
        },
        {
            breakpoint: '1199px',
            numVisible: 1,
            numScroll: 1
        },
        {
            breakpoint: '767px',
            numVisible: 1,
            numScroll: 1
        },
        {
            breakpoint: '575px',
            numVisible: 1,
            numScroll: 1
        }
    ];

    const productTemplate = (imagePath: string) => {
        return (
            <div className="p-carousel-item-content" style={{ textAlign: 'center' }}>
                <img
                    src={imagePath}
                    alt="Slide"
                    style={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: '400px',
                        objectFit: 'cover',
                        cursor: "pointer"
                    }}
                    onClick={() => {
                        setSelectedImage(imagePath);
                    }}
                />
            </div>
        );
    };

    const actionBodyTemplate = (rowData: BazarProduct) => {
        return (
            <div className="action-icons">
                {/* <Button label="View" outlined size="small" style={{ color: "#264164", borderColor: "#264164" }} onClick={() => handleEdit(rowData)} /> */}
                <Button label="Edit" outlined size="small" style={{ color: "#264164", borderColor: "#264164" }} onClick={() => handleEdit(rowData)} />
                <Button label="Remove" outlined size="small" style={{ color: "#264164", borderColor: "#264164" }} onClick={() => handleDelete(rowData)} />
            </div>
        );
    };

    async function getUserData() {
        try {
            let url;
            setDataLoad(true);
            url = `/usermobile/mobile/getbyid/${id}`;
            await apiFunctions
                .get(url)
                .then(async (response) => {
                    if (response && response.data && response.data.success) {
                        setUserdata(response.data.data);
                        formik.setValues({
                            honorifics: response.data?.data?.honorifics || "",
                            name: response.data?.data?.name || "",
                            namegujarati: response.data?.data?.namegujarati || "",
                            fathername: response.data?.data?.fathername || "",
                            fathernamegujarati: response.data?.data?.fathernamegujarati || "",
                            surnameid: response.data?.data?.surnameid || "",
                            subcastid: response.data?.data?.subcastid || null,
                            gender: response.data?.data?.gender || "",
                            dob: response.data?.data?.dob || null,
                            doa: response.data?.data?.doa || null,
                            isalive: response.data?.data?.isalive ?? true,
                            dod: response.data?.data?.dod || null,
                            maritalstatus: response.data?.data?.maritalstatus || "",
                            bloodgroup: response.data?.data?.bloodgroup || "",
                            mobileno: response.data?.data?.mobileno || "",
                            bio: response.data?.data?.bio || "",
                            homeaddress: response.data?.data?.homeaddress || "",
                            profiletype: response.data?.data?.profiletype || "",
                            nativeplaceid: response.data?.data?.nativeplaceid || "",
                            nativelat: response.data?.data?.nativelat || null,
                            nativelng: response.data?.data?.nativelng || null,
                            nativeaddress: response.data?.data?.nativeaddress || "",
                            nativecity: response.data?.data?.nativecity || "",
                            currentplaceid: response.data?.data?.currentplaceid || "",
                            currentlat: response.data?.data?.currentlat || null,
                            currentlng: response.data?.data?.currentlng || null,
                            currentaddress: response.data?.data?.currentaddress || "",
                            currentcity: response.data?.data?.currentcity || "",
                        });
                        // Family tree / followers / followings / post /
                        // surname+subcast dropdown data are NOT fetched here
                        // anymore — this used to await all of them one after
                        // another before the page ever became interactive.
                        // Each now loads lazily: the accordion sections fetch
                        // on first open (see the Accordion's onTabOpen
                        // below), and the surname/subcast dropdowns fetch on
                        // entering edit mode (see the isEditMode-gated
                        // useEffects above).
                        if (response.data.data?.posts > 0) {
                            setPostCurrentid(response.data.data?.latestpostid);
                        }
                    } else {
                        showToast({
                            severity: "error",
                            summary: "Error",
                            detail: response.data.message,
                            life: 3000,
                        });
                    }
                })
                .catch((err: AxiosError) => {
                    errorHandler(err);
                })
                .finally(() => {
                    setDataLoad(false);
                    setLoad(false);
                });
        } catch (error) {
            console.log(error);
        }
    }

    async function getUserFollowersData() {
        try {
            let url;
            // Loads lazily (see CollapsibleSection) — doesn't touch dataLoad,
            // which gates the Basic Info skeletons above; toggling it here
            // would flash already-loaded content back to a skeleton.
            if (name !== undefined && name !== null && name !== "" && name.length > 1) {
                url = `/user/followerslist/${id}/${skipFollowersData}/${itemsFollowersPerPage}/${name} `;
            } else {
                url = `/user/followerslist/${id}/${skipFollowersData}/${itemsFollowersPerPage}`;
            }
            await apiFunctions
                .get(url)
                .then((response) => {
                    if (response && response.data && response.data.success) {
                        settotalFollowersRecords(response.data.totalRecords);
                        let totalpage: any = Math.ceil(
                            parseInt(response.data.totalRecords) / parseInt(itemsFollowersPerPage)
                        );
                        settotalFollowersPages(totalpage);
                        setFollowersData(response.data.data);
                        // setProfileData(CountryFactory.createFromJsonArray(response.data.data));
                    } else {
                        showToast({
                            severity: "error",
                            summary: "Error",
                            detail: response.data.message,
                            life: 3000,
                        });
                    }
                })
                .catch((err: AxiosError) => {
                    errorHandler(err);
                });
        } catch (error) {
            console.log(error);
        }
    }

    async function getUserFollowingsData() {
        try {
            let url;
            // Loads lazily — see getUserFollowersData above.
            if (name !== undefined && name !== null && name !== "" && name.length > 1) {
                url = `/user/followinglist/${id}/${skipFollowingsData}/${itemsFollowingsPerPage}/${name} `;
            } else {
                url = `/user/followinglist/${id}/${skipFollowingsData}/${itemsFollowingsPerPage}`;
            }
            await apiFunctions
                .get(url)
                .then((response) => {
                    if (response && response.data && response.data.success) {
                        settotalFollowingsRecords(response.data.totalRecords);
                        let totalpage: any = Math.ceil(
                            parseInt(response.data.totalRecords) / parseInt(itemsFollowingsPerPage)
                        );
                        settotalFollowingsPages(totalpage);
                        setFollowingData(response.data.data);
                    } else {
                        showToast({
                            severity: "error",
                            summary: "Error",
                            detail: response.data.message,
                            life: 3000,
                        });
                    }
                })
                .catch((err: AxiosError) => {
                    errorHandler(err);
                });
        } catch (error) {
            console.log(error);
        }
    }

    async function getUserLikesData(id: any) {
        try {
            let url;
            // Loads lazily — see getUserFollowersData above.
            if (name !== undefined && name !== null && name !== "" && name.length > 1) {
                url = `/post/like/${id}/${skipLikesData}/${itemsLikesPerPage}/${name} `;
            } else {
                url = `/post/like/${id}/${skipLikesData}/${itemsLikesPerPage}`;
            }
            await apiFunctions
                .get(url)
                .then((response) => {
                    if (response && response.data && response.data.success) {
                        settotalLikesRecords(response.data.totalRecords);
                        let totalpage: any = Math.ceil(
                            parseInt(response.data.totalRecords) / parseInt(itemsLikesPerPage)
                        );
                        settotalLikesPages(totalpage);
                        setLikeData(response.data.data);
                    } else {
                        showToast({
                            severity: "error",
                            summary: "Error",
                            detail: response.data.message,
                            life: 3000,
                        });
                    }
                })
                .catch((err: AxiosError) => {
                    errorHandler(err);
                });
        } catch (error) {
            console.log(error);
        }
    }

    async function getUserCommentsData(id: any) {
        try {
            let url;
            // Loads lazily — see getUserFollowersData above.
            if (name !== undefined && name !== null && name !== "" && name.length > 1) {
                url = `/post/comment/${id}/${skipCommentsData}/${itemsCommentsPerPage}/${name} `;
            } else {
                url = `/post/comment/${id}/${skipCommentsData}/${itemsCommentsPerPage}`;
            }
            await apiFunctions
                .get(url)
                .then((response) => {
                    if (response && response.data && response.data.success) {
                        settotalCommentsRecords(response.data.totalRecords);
                        let totalpage: any = Math.ceil(
                            parseInt(response.data.totalRecords) / parseInt(itemsCommentsPerPage)
                        );
                        settotalCommentsPages(totalpage);
                        setCommentData(response.data.data);
                    } else {
                        showToast({
                            severity: "error",
                            summary: "Error",
                            detail: response.data.message,
                            life: 3000,
                        });
                    }
                })
                .catch((err: AxiosError) => {
                    errorHandler(err);
                });
        } catch (error) {
            console.log(error);
        }
    }

    async function getUserFamilyTreeData() {
        try {
            let url;
            // Loads lazily — see getUserFollowersData above.
            url = `/usermobile/mobile/getfullfamilytree/${id}`;
            await apiFunctions
                .get(url)
                .then((response) => {
                    if (response && response.data && response.data.success) {
                        setFamilyTreeData(response.data.data);
                    } else {
                        showToast({
                            severity: "error",
                            summary: "Error",
                            detail: response.data.message,
                            life: 3000,
                        });
                    }
                })
                .catch((err: AxiosError) => {
                    errorHandler(err);
                });
        } catch (error) {
            console.log(error);
        }
    }

    async function getUserPost(id: any) {
        try {
            let url;
            // Loads lazily — see getUserFollowersData above.
            url = `/post/details/${id}`;
            await apiFunctions
                .get(url)
                .then(async (response) => {
                    if (response && response.data && response.data.success) {
                        setPostLike(response.data.data?.likes);
                        setPostComment(response.data.data?.comments);
                        setImages(response.data.data?.images);
                        setPostDescription(response.data.data?.description);
                        setPostDate(new Date(response.data.data?.createddate))
                        setPostLatestid(response.data.data?.latestpostid);
                        setPostFirstid(response.data.data?.firstpostid);
                        setPostPrevid(response.data.data?.previouspostid);
                        setPostNextid(response.data.data?.nextpostid);

                        if (response.data.data?.likes > 0) {
                            await getUserLikesData(id);
                        }
                        if (response.data.data?.comments > 0) {
                            await getUserCommentsData(id);
                        }
                    } else {
                        showToast({
                            severity: "error",
                            summary: "Error",
                            detail: response.data.message,
                            life: 3000,
                        });
                    }
                })
                .catch((err: AxiosError) => {
                    errorHandler(err);
                });
        } catch (error) {
            console.log(error);
        }
    }

    // Each fires the first time its accordion tab opens, not on page load —
    // guarded so re-opening an already-loaded tab doesn't refetch.
    const handleFamilyTreeTabOpen = () => {
        if (familyTreeLoaded) return;
        setFamilyTreeLoaded(true);
        getUserFamilyTreeData();
    };
    const handleFollowersTabOpen = () => {
        if (followersLoaded) return;
        setFollowersLoaded(true);
        getUserFollowersData();
    };
    const handleFollowingsTabOpen = () => {
        if (followingsLoaded) return;
        setFollowingsLoaded(true);
        getUserFollowingsData();
    };
    const handlePostTabOpen = () => {
        if (postLoaded || !postCurrentid) return;
        setPostLoaded(true);
        getUserPost(postCurrentid);
    };

    const handleEdit = (rowData: BazarProduct) => {
    };

    const handleDelete = (rowData: BazarProduct) => {
    };

    const getsearchdata = (e: any) => {
        if (e.target.value.length >= 2) {
            setName(e.target.value);
        } else {
            setName("");
            setLoad(false);
        }
    };

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            getUserData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if ((window as any).google?.maps?.places) {
            setGoogleMapsLoaded(true);
            return;
        }
        const existing = document.querySelector('script[src*="maps.googleapis.com"]');
        if (existing) {
            existing.addEventListener('load', () => setGoogleMapsLoaded(true));
            return;
        }
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAP_KEY}&libraries=places`;
        script.async = true;
        script.onload = () => setGoogleMapsLoaded(true);
        document.head.appendChild(script);
    }, []);

    useEffect(() => {
        if (!isEditMode || !googleMapsLoaded) return;

        const timer = setTimeout(() => {
            if (nativePlaceInputRef.current) {
                const ac = new (window as any).google.maps.places.Autocomplete(nativePlaceInputRef.current);
                ac.addListener('place_changed', () => {
                    const place = ac.getPlace();
                    formik.setFieldValue('nativeplaceid', place.place_id || '');
                    formik.setFieldValue('nativelat', place.geometry?.location?.lat() ?? null);
                    formik.setFieldValue('nativelng', place.geometry?.location?.lng() ?? null);
                    formik.setFieldValue('nativeaddress', place.formatted_address || '');
                    const city = place.address_components?.find((c: any) =>
                        c.types.includes('locality') || c.types.includes('administrative_area_level_3')
                    );
                    formik.setFieldValue('nativecity', city?.long_name || '');
                });
            }

            if (currentPlaceInputRef.current) {
                const ac = new (window as any).google.maps.places.Autocomplete(currentPlaceInputRef.current);
                ac.addListener('place_changed', () => {
                    const place = ac.getPlace();
                    formik.setFieldValue('currentplaceid', place.place_id || '');
                    formik.setFieldValue('currentlat', place.geometry?.location?.lat() ?? null);
                    formik.setFieldValue('currentlng', place.geometry?.location?.lng() ?? null);
                    formik.setFieldValue('currentaddress', place.formatted_address || '');
                    const city = place.address_components?.find((c: any) =>
                        c.types.includes('locality') || c.types.includes('administrative_area_level_3')
                    );
                    formik.setFieldValue('currentcity', city?.long_name || '');
                });
            }
        }, 100);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode, googleMapsLoaded]);

    const containerRef = useRef<HTMLDivElement | null>(null); // Reference to the scroll container
    const isDragging = useRef(false); // Track dragging state
    const startX = useRef(0); // Initial X position of the mouse
    const startY = useRef(0); // Initial Y position of the mouse
    const scrollLeft = useRef(0); // Initial horizontal scroll position
    const scrollTop = useRef(0); // Initial vertical scroll position

    const handleMouseMove = (e: any) => {
        if (!isDragging.current || !containerRef.current) return; // Safeguard for null

        const dx = e.clientX - startX.current; // Change in X position
        const dy = e.clientY - startY.current; // Change in Y position

        containerRef.current.scrollLeft = scrollLeft.current - dx; // Update horizontal scroll
        containerRef.current.scrollTop = scrollTop.current - dy; // Update vertical scroll
    };

    const handleMouseUp = () => {
        if (!containerRef.current) return; // Safeguard for null
        isDragging.current = false;
        containerRef.current.style.cursor = "grab"; // Reset cursor style
    };

    useEffect(() => {
        // Attach the mousemove and mouseup events at the document level
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);

        return () => {
            // Clean up event listeners
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, []);

    const commentheader = (
        <>
            <div className="grid grid-cols-12 gap-4 items-center table-header">
                <div className="col-span-12 sm:col-span-6">
                    <h2 className="card-title m-0 text-lg sm:text-xl">Comments</h2>
                </div>
                <div className="col-span-12 sm:col-span-6">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-end">
                        <span className="text-sm sm:text-base whitespace-nowrap">
                            Total Comments – {postComment}
                        </span>
                        <div className="search-box flex items-center gap-2 w-full sm:w-auto">
                            <span className="text-sm text-gray-600">Search:</span>
                            <PrimeInputText
                                type="text"
                                className="search-input w-full sm:w-auto"
                                onChange={(e) => getsearchdata(e)}
                            />
                        </div>

                    </div>
                </div>

            </div>
        </>
    );

    const follwersheader = (
        <>
            <div className="grid grid-cols-12 gap-4 items-center table-header">
                <div className="col-span-12 sm:col-span-6">
                    <h2 className="card-title m-0 text-lg sm:text-xl">Followers</h2>
                </div>
                <div className="col-span-12 sm:col-span-6">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-end">
                        <span className="text-sm sm:text-base whitespace-nowrap">
                            Total Followers – {totalFollowersRecords}
                        </span>
                        <div className="search-box flex items-center gap-2 w-full sm:w-auto">
                            <span className="text-sm text-gray-600">Search:</span>
                            <PrimeInputText
                                type="text"
                                className="search-input w-full sm:w-auto"
                                onChange={(e) => getsearchdata(e)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    const followingheader = (
        <>
            <div className="grid grid-cols-12 gap-4 items-center table-header">
                <div className="col-span-12 sm:col-span-6">
                    <h2 className="card-title m-0 text-lg sm:text-xl">Followings</h2>
                </div>
                <div className="col-span-12 sm:col-span-6">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-end">
                        <span className="text-sm sm:text-base whitespace-nowrap">
                            Total Followings – {totalFollowingRecords}
                        </span>
                        <div className="search-box flex items-center gap-2 w-full sm:w-auto">
                            <span className="text-sm text-gray-600">Search:</span>
                            <PrimeInputText
                                type="text"
                                className="search-input w-full sm:w-auto"
                                onChange={(e) => getsearchdata(e)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    const likeheader = (
        <>
            <div className="grid grid-cols-12 gap-4 items-center table-header">
                <div className="col-span-12 sm:col-span-6">
                    <h2 className="card-title m-0 text-lg sm:text-xl">Likes</h2>
                </div>
                <div className="col-span-12 sm:col-span-6">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-end">
                        <span className="text-sm sm:text-base whitespace-nowrap">
                            Total Likes – {postLike}
                        </span>
                        <div className="search-box flex items-center gap-2 w-full sm:w-auto">
                            <span className="text-sm text-gray-600">Search:</span>
                            <PrimeInputText
                                type="text"
                                className="search-input w-full sm:w-auto"
                                onChange={(e) => getsearchdata(e)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    const paginatoreFollowingsTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport",
        RowsPerPageDropdown: (options: PaginatorRowsPerPageDropdownOptions) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 25, value: 25 },
                { label: 50, value: 50 },
                { label: 100, value: 100 },
                { label: 200, value: 200 },
            ];

            return (
                <React.Fragment>
                    <span className="mx-1">Show </span>
                    <Dropdown
                        value={itemsPerPage}
                        options={dropdownOptions}
                        onChange={(e) => handleItemsFollowingsPerPageChange(e.value)}
                    />
                </React.Fragment>
            );
        },
        CurrentPageReport: (options: PaginatorCurrentPageReportOptions) => {
            return <span className="entries">entries</span>;
        },
    };

    const paginatoreFollowersTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport",
        RowsPerPageDropdown: (options: PaginatorRowsPerPageDropdownOptions) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 25, value: 25 },
                { label: 50, value: 50 },
                { label: 100, value: 100 },
                { label: 200, value: 200 },
            ];

            return (
                <React.Fragment>
                    <span className="mx-1">Show </span>
                    <Dropdown
                        value={itemsPerPage}
                        options={dropdownOptions}
                        onChange={(e) => handleItemsFollowersPerPageChange(e.value)}
                    />
                </React.Fragment>
            );
        },
        CurrentPageReport: (options: PaginatorCurrentPageReportOptions) => {
            return <span className="entries">entries</span>;
        },
    };

    const handleFollowersPageChange = (newPage: number) => {
        setCurrentFollowersPage(newPage);
    };

    const handleItemsFollowersPerPageChange = (value: number) => {
        setItemsFollowersPerPage(value);
        setCurrentFollowersPage(0);
    };

    useEffect(() => {
        setSkipFollowingsData(currentFollowingsPage * itemsFollowingsPerPage);
    }, [currentFollowingsPage, itemsFollowingsPerPage]);

    useEffect(() => {
        if (!load) {
            getUserFollowingsData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skipFollowingsData, itemsFollowingsPerPage]);

    useEffect(() => {
        setSkipFollowesData(currentFollowersPage * itemsFollowersPerPage);
    }, [currentFollowersPage, itemsFollowersPerPage]);

    useEffect(() => {
        if (!load) {
            getUserFollowersData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skipFollowersData, itemsFollowersPerPage]);

    useEffect(() => {
        setSkipCommentsData(currentCommentsPage * itemsCommentsPerPage);
    }, [currentCommentsPage, itemsCommentsPerPage]);

    useEffect(() => {
        if (!load) {
            getUserCommentsData(postCurrentid);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCommentsPage, itemsCommentsPerPage]);

    useEffect(() => {
        setSkipLikesData(currentLikesPage * itemsLikesPerPage);
    }, [currentLikesPage, itemsLikesPerPage]);

    useEffect(() => {
        if (!load) {
            getUserLikesData(postCurrentid);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLikesPage, itemsLikesPerPage]);

    const handleFollowingsPageChange = (newPage: number) => {
        setCurrentFollowingsPage(newPage);
    };

    const handleItemsFollowingsPerPageChange = (value: number) => {
        setItemsFollowingsPerPage(value);
        setCurrentFollowingsPage(0);
    };

    const paginatoreLikesTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport",
        RowsPerPageDropdown: (options: PaginatorRowsPerPageDropdownOptions) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 25, value: 25 },
                { label: 50, value: 50 },
                { label: 100, value: 100 },
                { label: 200, value: 200 },
            ];

            return (
                <React.Fragment>
                    <span className="mx-1">Show </span>
                    <Dropdown
                        value={itemsPerPage}
                        options={dropdownOptions}
                        onChange={(e) => handleItemsLikesPerPageChange(e.value)}
                    />
                </React.Fragment>
            );
        },
        CurrentPageReport: (options: PaginatorCurrentPageReportOptions) => {
            return <span className="entries">entries</span>;
        },
    };

    const paginatoreCommentsTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport",
        RowsPerPageDropdown: (options: PaginatorRowsPerPageDropdownOptions) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 25, value: 25 },
                { label: 50, value: 50 },
                { label: 100, value: 100 },
                { label: 200, value: 200 },
            ];

            return (
                <React.Fragment>
                    <span className="mx-1">Show </span>
                    <Dropdown
                        value={itemsPerPage}
                        options={dropdownOptions}
                        onChange={(e) => handleItemsCommentsPerPageChange(e.value)}
                    />
                </React.Fragment>
            );
        },
        CurrentPageReport: (options: PaginatorCurrentPageReportOptions) => {
            return <span className="entries">entries</span>;
        },
    };

    const handleLikesPageChange = (newPage: number) => {
        setCurrentLikesPage(newPage);
    };

    const handleItemsLikesPerPageChange = (value: number) => {
        setItemsLikesPerPage(value);
        setCurrentLikesPage(0);
    };

    const handleCommentsPageChange = (newPage: number) => {
        setCurrentCommentsPage(newPage);
    };

    const handleItemsCommentsPerPageChange = (value: number) => {
        setItemsCommentsPerPage(value);
        setCurrentCommentsPage(0);
    };

    const handleClosePreview = () => {
        setSelectedImage(null);
    }

    const getPostData = (id: any) => {
        getUserPost(id);
        setPostCurrentid(id);
    }

    const textBodyTemplate = (rowData: any) => {
        return (
            <div
                title={rowData.text}
                style={{
                    maxWidth: '200px', // Adjust as needed
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {rowData.text}
            </div>
        );
    };

    const commentDateBodyTemplate = (rowData: any) => {
        const formattedDate = DateFormate.formatDateTime(rowData.createddate); // or rowData.createddate
        return (
            <span>
                {formattedDate}
            </span>
        );
    };

    // const getsearchdata = (e: any) => {
    //     if (e.target.value.length >= 2) {
    //         setName(e.target.value);
    //     } else {
    //         setName("");
    //         setLoad(false);
    //     }
    // };

    // useEffect(() => {
    //     setSkipData(currentPage * itemsPerPage);
    // }, [currentPage, itemsPerPage]);

    // useEffect(() => {
    //     if (!load) {
    //         getProfileData();
    //     }
    // }, [skipData, itemsPerPage, name, sortField, sortOrder]);

    const RowSkeleton = () => (
        <div className="flex flex-col sm:flex-row gap-2 mt-1">
            <div className="w-40">
                <Skeleton height="1.5rem" className="rounded" />
            </div>
            <div className="flex-1">
                <Skeleton height="1.5rem" className="rounded" />
            </div>
        </div>
    );

    const ChecklistSkeleton = () => (
        <div className="flex items-center gap-2">
            <Skeleton shape="circle" size="24px" />
            <Skeleton height="1rem" width="120px" />
        </div>
    );

    const handleProfileTypeChange = async (type: string) => {
        let payload = {
            profiletype: type
        }
        await apiFunctions.patch(`/subadmin/change-profiletype/${id}`, payload)
            .then((response) => {
                if (response && response.data && response.data.success) {
                    showToast({
                        severity: "success",
                        summary: "Success",
                        detail: response.data.message,
                        life: 3000,
                    });
                } else {
                    showToast({
                        severity: "error",
                        summary: "Error",
                        detail: response.data.message,
                        life: 3000,
                    });
                }
            })
            .catch((err: AxiosError) => {
                if (err.status === 401) {
                    dispatch(logout());
                    persistor.purge();
                    navigate("/admin/login");
                }
                errorHandler(err);
            }).finally(() => {
                getUserData();
            });
    }

    const handleApprove = async () => {
        await apiFunctions.put(`/subadminmobile/mobile/approverequest/${id}`, {})
            .then((response) => {
                if (response && response.data && response.data.success) {
                    showToast({
                        severity: "success",
                        summary: "Success",
                        detail: response.data.message,
                        life: 3000,
                    });
                } else {
                    showToast({
                        severity: "error",
                        summary: "Error",
                        detail: response.data.message,
                        life: 3000,
                    });
                }
            })
            .catch((err: AxiosError) => {
                if (err.status === 401) {
                    dispatch(logout());
                    persistor.purge();
                    navigate("/admin/login");
                }
                errorHandler(err);
            }).finally(() => {
                getUserData();
            });
    }

    // Identity Info has its own self-contained edit mode — separate from
    // the big Basic Info formik/isEditMode, since it's a different card
    // with its own Edit button.
    const [identityEditMode, setIdentityEditMode] = useState(false);
    const [identityAdharno, setIdentityAdharno] = useState("");
    const [identityFrontFile, setIdentityFrontFile] = useState<File | null>(null);
    const [identityBackFile, setIdentityBackFile] = useState<File | null>(null);
    const [identityFrontPreview, setIdentityFrontPreview] = useState<string | null>(null);
    const [identityBackPreview, setIdentityBackPreview] = useState<string | null>(null);
    const [savingIdentity, setSavingIdentity] = useState(false);

    const startIdentityEdit = () => {
        setIdentityAdharno(userdata?.adharno || "");
        setIdentityFrontFile(null);
        setIdentityBackFile(null);
        setIdentityFrontPreview(null);
        setIdentityBackPreview(null);
        setIdentityEditMode(true);
    };

    const handleIdentityFileChange = (file: File | null, side: "front" | "back") => {
        if (side === "front") {
            setIdentityFrontFile(file);
            setIdentityFrontPreview(file ? URL.createObjectURL(file) : null);
        } else {
            setIdentityBackFile(file);
            setIdentityBackPreview(file ? URL.createObjectURL(file) : null);
        }
    };

    const uploadIdentityImage = async (file: File, fieldname: "identityfrontimg" | "identitybackimg") => {
        const formData = new FormData();
        formData.append("file", file);
        await apiFunctions.post(
            `/usermobile/mobile/updateadminimage/userprofile/${fieldname}?id=${id}`,
            formData,
            "",
            true
        );
    };

    const handleSaveIdentity = async () => {
        setSavingIdentity(true);
        try {
            if (identityAdharno.trim() !== (userdata?.adharno || "")) {
                await apiFunctions.put(`/user/update-profile/${id}`, { adharno: identityAdharno.trim() });
            }
            if (identityFrontFile) await uploadIdentityImage(identityFrontFile, "identityfrontimg");
            if (identityBackFile) await uploadIdentityImage(identityBackFile, "identitybackimg");

            showToast({ severity: "success", summary: "Success", detail: "Identity info updated", life: 2500 });
            setIdentityEditMode(false);
            getUserData();
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setSavingIdentity(false);
        }
    };

    const [messageDialogOpen, setMessageDialogOpen] = useState(false);
    const [messageTitle, setMessageTitle] = useState("Sagar Samaj");
    const [messageText, setMessageText] = useState("");
    const [sendingMessage, setSendingMessage] = useState(false);

    const [sendType, setSendType] = useState<"instant" | "schedule">("instant");
    const [scheduleType, setScheduleType] = useState<string>("once");
    const [scheduledDate, setScheduledDate] = useState<any>(null);
    const [scheduledTime, setScheduledTime] = useState<string>("");
    const [monthDate, setMonthDate] = useState<number>(1);
    const [specificDays, setSpecificDays] = useState<string[]>([]);
    const [dayTimes, setDayTimes] = useState<{ day: string; time: string }[]>([]);

    const [waTemplate, setWaTemplate] = useState("");
    const [waParams, setWaParams] = useState<string[]>([]);
    const [sendingWhatsApp, setSendingWhatsApp] = useState(false);

    const sendTypeOptions = [
        { label: "Send Now", value: "instant" },
        { label: "Schedule", value: "schedule" }
    ];

    const scheduleTypeOptions = [
        { label: "One Time", value: "once" },
        { label: "Daily", value: "daily" },
        { label: "Weekly", value: "weekly" },
        { label: "Monthly", value: "monthly" },
        { label: "Specific Days", value: "specific_days" },
        { label: "Specific Days & Times", value: "day_time" }
    ];

    const weekDays = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

    const resetMessageDialog = () => {
        setMessageTitle("Sagar Samaj");
        setMessageText("");
        setSendType("instant");
        setScheduleType("once");
        setScheduledDate(null);
        setScheduledTime("");
        setMonthDate(1);
        setSpecificDays([]);
        setDayTimes([]);
        setWaTemplate("");
        setWaParams([]);
    };

    const [notificationHistory, setNotificationHistory] = useState<any[]>([]);
    const [notificationHistoryLoading, setNotificationHistoryLoading] = useState(false);
    const [notificationHistoryLoaded, setNotificationHistoryLoaded] = useState(false);

    const fetchNotificationHistory = async (force = false) => {
        if (notificationHistoryLoaded && !force) return;
        setNotificationHistoryLoading(true);
        try {
            const res = await apiFunctions.get("/activeusers/scheduled-notification-logs", {
                params: { userid: id, page: 1, limit: 50 },
            });
            if (res?.data?.success) {
                setNotificationHistory(res.data.data || []);
                setNotificationHistoryLoaded(true);
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setNotificationHistoryLoading(false);
        }
    };

    const [chatConversations, setChatConversations] = useState<any[]>([]);
    const [chatConversationsLoading, setChatConversationsLoading] = useState(false);
    const [chatConversationsLoaded, setChatConversationsLoaded] = useState(false);
    const [chatConversationSearch, setChatConversationSearch] = useState("");

    const [chatThreadPartner, setChatThreadPartner] = useState<any>(null);
    const [chatMessages, setChatMessages] = useState<any[]>([]);
    const [chatMessagesLoading, setChatMessagesLoading] = useState(false);
    const [chatLoadingMore, setChatLoadingMore] = useState(false);

    const [chatThreadPage, setChatThreadPage] = useState(1);
    const [chatThreadTotalPages, setChatThreadTotalPages] = useState(1);

    const [chatThreadSearchOpen, setChatThreadSearchOpen] = useState(false);
    const [chatThreadSearchQuery, setChatThreadSearchQuery] = useState("");
    const [chatThreadType, setChatThreadType] = useState("");
    const [chatSearchResults, setChatSearchResults] = useState<any[]>([]);
    const [chatSearchResultsLoading, setChatSearchResultsLoading] = useState(false);

    const [chatHighlightedMessageId, setChatHighlightedMessageId] = useState<string | null>(null);
    const chatMessageRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const chatMessagesContainerRef = useRef<HTMLDivElement>(null);
    const chatScrollActionRef = useRef<"bottom" | "preserve" | null>(null);
    const chatPreserveScrollRef = useRef<{ height: number; top: number } | null>(null);
    // While true, any image/video/map-embed that finishes loading inside the
    // thread re-pins the scroll to the bottom — those grow taller after the
    // initial scrollTop=scrollHeight, so without this the view lands short
    // of the actual latest message whenever it's IMAGE/VIDEO/LOCATION.
    const chatStickToBottomRef = useRef(false);

    const fetchChatConversations = async () => {
        if (chatConversationsLoaded) return;
        setChatConversationsLoading(true);
        try {
            const res = await apiFunctions.get(`/adminchat/conversations/${id}`);
            if (res?.data?.success) {
                setChatConversations(res.data.data || []);
                setChatConversationsLoaded(true);
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setChatConversationsLoading(false);
        }
    };

    // Which partner's thread is currently on screen — every async fetch
    // below is stamped with the partner_id it was issued for, and discards
    // its own result if the admin has since switched to a different thread
    // (e.g. a "load more" fired for A, then the admin switches to B before it
    // resolves; without this guard, A's late result would splice into B's
    // messages and clobber B's fresh scroll-to-bottom with A's stale
    // preserved scroll position).
    const activeChatThreadKeyRef = useRef<string | null>(null);

    // Plain pagination of the real thread — search/type only drive the
    // separate chatSearchResults dropdown, never filter this directly.
    const fetchChatThreadMessages = async (partner: any, page: number, append: boolean) => {
        const requestKey = partner.partner_id;
        if (append) {
            setChatLoadingMore(true);
            const el = chatMessagesContainerRef.current;
            chatPreserveScrollRef.current = el ? { height: el.scrollHeight, top: el.scrollTop } : null;
            chatScrollActionRef.current = "preserve";
        } else {
            setChatMessagesLoading(true);
            chatScrollActionRef.current = "bottom";
        }
        try {
            const res = await apiFunctions.get(`/adminchat/messages/${id}/${partner.partner_id}`, {
                params: { page, limit: 20 },
            });
            if (activeChatThreadKeyRef.current !== requestKey) return; // stale — a different thread is open now
            if (res?.data?.success) {
                setChatMessages((prev) => (append ? [...prev, ...res.data.data] : res.data.data) || []);
                setChatThreadPage(res.data.page || page);
                setChatThreadTotalPages(res.data.totalPages || 1);
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            if (activeChatThreadKeyRef.current === requestKey) {
                setChatMessagesLoading(false);
                setChatLoadingMore(false);
            }
        }
    };

    const openChatThread = (partner: any, jumpToId?: string) => {
        activeChatThreadKeyRef.current = partner.partner_id;
        setChatThreadPartner(partner);
        setChatThreadSearchOpen(false);
        setChatThreadSearchQuery("");
        setChatThreadType("");
        setChatSearchResults([]);
        setChatMessages([]);
        if (jumpToId) jumpToChatMessage(partner, jumpToId);
        else fetchChatThreadMessages(partner, 1, false);
    };

    const loadMoreChatMessages = () => {
        if (chatThreadPartner && chatThreadPage < chatThreadTotalPages && !chatLoadingMore && !chatMessagesLoading) {
            fetchChatThreadMessages(chatThreadPartner, chatThreadPage + 1, true);
        }
    };

    const handleChatThreadScroll = () => {
        const el = chatMessagesContainerRef.current;
        if (el && el.scrollTop < 80) loadMoreChatMessages();
    };

    useEffect(() => {
        const el = chatMessagesContainerRef.current;
        // Skip the transient empty-array render from openChatThread's
        // setChatMessages([]) — acting on it here would consume/reset the
        // "bottom" flag before the real messages ever arrive.
        if (!el || chatMessages.length === 0) return;
        if (chatScrollActionRef.current === "bottom") {
            el.scrollTop = el.scrollHeight;
            chatStickToBottomRef.current = true;
            setTimeout(() => { chatStickToBottomRef.current = false; }, 1500);
        } else if (chatScrollActionRef.current === "preserve" && chatPreserveScrollRef.current) {
            const grew = el.scrollHeight - chatPreserveScrollRef.current.height;
            el.scrollTop = chatPreserveScrollRef.current.top + grew;
        }
        chatScrollActionRef.current = null;
    }, [chatMessages]);

    // Fires on every image `load`, iframe `load` (map embed), and video
    // `loadeddata` inside the thread (capture phase catches these non-
    // bubbling events even though they fire deep inside ChatMessageContent).
    const handleChatThreadMediaLoad = () => {
        const el = chatMessagesContainerRef.current;
        if (el && chatStickToBottomRef.current) el.scrollTop = el.scrollHeight;
    };

    // WhatsApp-style: results appear in a dropdown as you type/pick a type
    // tab — the main thread stays untouched until a result is tapped.
    useEffect(() => {
        if (!chatThreadPartner || !chatThreadSearchOpen) return;
        if (!chatThreadSearchQuery.trim() && !chatThreadType) { setChatSearchResults([]); return; }
        setChatSearchResultsLoading(true);
        const t = setTimeout(async () => {
            try {
                const res = await apiFunctions.get(`/adminchat/messages/${id}/${chatThreadPartner.partner_id}`, {
                    params: { page: 1, limit: 20, search: chatThreadSearchQuery.trim(), type: chatThreadType },
                });
                if (res?.data?.success) setChatSearchResults(res.data.data || []);
            } catch (err) {
                errorHandler(err as AxiosError);
            } finally {
                setChatSearchResultsLoading(false);
            }
        }, 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatThreadSearchQuery, chatThreadType, chatThreadSearchOpen]);

    // Tap a search result → land on it in context (a window of real thread
    // around it), then scroll to + flash-highlight the bubble.
    const jumpToChatMessage = async (partner: any, messageId: string) => {
        const requestKey = partner.partner_id;
        setChatThreadSearchOpen(false);
        setChatSearchResults([]);
        setChatMessagesLoading(true);
        chatScrollActionRef.current = null;
        try {
            const res = await apiFunctions.get(`/adminchat/messages/${id}/${partner.partner_id}`, {
                params: { aroundId: messageId },
            });
            if (activeChatThreadKeyRef.current !== requestKey) return; // stale — a different thread is open now
            if (res?.data?.success) {
                setChatMessages(res.data.data || []);
                setChatThreadPage(1);
                setChatThreadTotalPages(1);
                setChatHighlightedMessageId(res.data.targetId || messageId);
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            if (activeChatThreadKeyRef.current === requestKey) setChatMessagesLoading(false);
        }
    };

    useEffect(() => {
        if (!chatHighlightedMessageId) return;
        const el = chatMessageRefs.current[chatHighlightedMessageId];
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
        const t = setTimeout(() => setChatHighlightedMessageId(null), 2200);
        return () => clearTimeout(t);
    }, [chatHighlightedMessageId, chatMessages]);

    const visibleChatConversations = chatConversations.filter((c: any) => {
        const q = chatConversationSearch.trim().toLowerCase();
        if (!q) return true;
        return `${c.name} ${c.surname}`.toLowerCase().includes(q);
    });

    const groupedChatSearchResults = (() => {
        const groups: { label: string; items: any[] }[] = [];
        for (const m of chatSearchResults) {
            const label = dateLabel(m.createddate);
            const last = groups[groups.length - 1];
            if (last && last.label === label) last.items.push(m);
            else groups.push({ label, items: [m] });
        }
        return groups;
    })();

    const chatResultPreview = (m: any) => {
        switch (m.message_type) {
            case "IMAGE": case "CAMERA": return "📷 Photo";
            case "VIDEO": return "🎥 Video";
            case "AUDIO": return "🎵 Audio";
            case "FILE": return `📄 ${m.file_name || "Document"}`;
            case "LOCATION": return "📍 Location";
            default: return m.message;
        }
    };

    // Global search — same as the standalone Chat module: finds users AND
    // messages across the whole platform, not just this user's own chats.
    const [chatGlobalQuery, setChatGlobalQuery] = useState("");
    const [chatGlobalOpen, setChatGlobalOpen] = useState(false);
    const [chatGlobalUsers, setChatGlobalUsers] = useState<any[]>([]);
    const [chatGlobalMessages, setChatGlobalMessages] = useState<any[]>([]);
    const [chatGlobalLoading, setChatGlobalLoading] = useState(false);
    const chatGlobalSearchRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!chatGlobalQuery.trim()) { setChatGlobalUsers([]); setChatGlobalMessages([]); return; }
        setChatGlobalLoading(true);
        const t = setTimeout(async () => {
            try {
                const res = await apiFunctions.get("/adminchat/search", { params: { q: chatGlobalQuery.trim() } });
                if (res?.data?.success) {
                    setChatGlobalUsers(res.data.users || []);
                    setChatGlobalMessages(res.data.messages || []);
                }
            } catch (err) {
                errorHandler(err as AxiosError);
            } finally {
                setChatGlobalLoading(false);
            }
        }, 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatGlobalQuery]);

    useEffect(() => {
        const onClickOutside = (e: MouseEvent) => {
            if (chatGlobalSearchRef.current && !chatGlobalSearchRef.current.contains(e.target as Node)) {
                setChatGlobalOpen(false);
            }
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    const goToUserProfileFromChat = (userId: string) => {
        setChatGlobalOpen(false);
        setChatGlobalQuery("");
        if (userId === id) return; // already here
        navigate(`/admin/profiles/view/${userId}`);
    };

    // A global message result either involves this profile's own user
    // (open it right here, in this panel) or two unrelated users (this page
    // can't show that pair, so land on the other participant's profile
    // instead — the closest equivalent place to view it).
    const goToGlobalChatMessage = (msg: any) => {
        setChatGlobalOpen(false);
        setChatGlobalQuery("");
        if (msg.sender.id === id || msg.receiver.id === id) {
            const partner = msg.sender.id === id ? msg.receiver : msg.sender;
            openChatThread(partner, msg._id);
        } else {
            navigate(`/admin/profiles/view/${msg.sender.id}`);
        }
    };

    const deleteChatMessage = (messageId: string) => {
        confirmDialog({
            header: "Delete Message",
            message: (
                <div className="flex items-center gap-3">
                    <TrashIcon className="w-5 h-5" style={{ color: "#ef4444" }} />
                    <span>Delete this message? This cannot be undone.</span>
                </div>
            ),
            accept: async () => {
                try {
                    const res = await apiFunctions.delete(`/adminchat/message/${messageId}`);
                    if (res?.data?.success) {
                        setChatMessages((prev) => prev.filter((m) => m._id !== messageId));
                        showToast({ severity: "success", summary: "Deleted", detail: "Message deleted", life: 2000 });
                    }
                } catch (err) {
                    errorHandler(err as AxiosError);
                }
            },
        });
    };

    const deleteChatConversation = () => {
        if (!chatThreadPartner) return;
        confirmDialog({
            header: "Delete Conversation",
            message: (
                <div className="flex items-center gap-3">
                    <TrashIcon className="w-5 h-5" style={{ color: "#ef4444" }} />
                    <span>Delete the entire conversation with {chatThreadPartner.name} {chatThreadPartner.surname || ""}? This cannot be undone.</span>
                </div>
            ),
            accept: async () => {
                try {
                    const res = await apiFunctions.delete(`/adminchat/conversation/${id}/${chatThreadPartner.partner_id}`);
                    if (res?.data?.success) {
                        showToast({ severity: "success", summary: "Deleted", detail: "Conversation deleted", life: 2000 });
                        activeChatThreadKeyRef.current = null;
                        setChatThreadPartner(null);
                        setChatMessages([]);
                        setChatConversationsLoaded(false);
                        fetchChatConversations();
                    }
                } catch (err) {
                    errorHandler(err as AxiosError);
                }
            },
        });
    };

    const handleSendMessage = async () => {
        setSendingMessage(true);
        try {
            const response = await apiFunctions.post("/activeusers/notifications", {
                userIds: [id],
                title: messageTitle.trim(),
                message: messageText.trim(),
                send_type: sendType,
                target_type: "selected_users",
                schedule_type: sendType === "schedule" ? scheduleType : "",
                scheduled_date: scheduledDate ? new Date(scheduledDate) : null,
                scheduled_time: scheduledTime,
                month_date: monthDate,
                specific_days: specificDays,
                day_times: dayTimes,
                whatsapp_template: waTemplate,
                whatsapp_params: waParams,
            });
            if (response?.data?.success) {
                showToast({ severity: "success", summary: "Sent", detail: sendType === "schedule" ? "Notification scheduled" : "Message sent to user", life: 2500 });
                setMessageDialogOpen(false);
                resetMessageDialog();
                if (notificationHistoryLoaded) fetchNotificationHistory(true);
            } else {
                showToast({ severity: "error", summary: "Error", detail: response?.data?.message || "Failed to send", life: 3000 });
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setSendingMessage(false);
        }
    };

    const handleSendWhatsApp = async () => {
        if (!waTemplate) {
            showToast({ severity: "warn", summary: "Warning", detail: "Please select a WhatsApp template", life: 3000 });
            return;
        }
        setSendingWhatsApp(true);
        try {
            const response = await apiFunctions.post("/activeusers/whatsapp-notifications", {
                userIds: [id],
                target_type: "selected_users",
                template: waTemplate,
                params: waParams,
            });
            if (response?.data?.success) {
                showToast({ severity: "success", summary: "Sent", detail: "WhatsApp message sent to user", life: 2500 });
                setMessageDialogOpen(false);
                resetMessageDialog();
                if (notificationHistoryLoaded) fetchNotificationHistory(true);
            } else {
                showToast({ severity: "error", summary: "Error", detail: response?.data?.message || "Failed to send", life: 3000 });
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setSendingWhatsApp(false);
        }
    };

    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState("");

    const handleReject = async () => {
        await apiFunctions.put(`/subadminmobile/mobile/rejectrequest/${id}`, { reason: rejectReason.trim() })
            .then((response) => {
                if (response && response.data && response.data.success) {
                    showToast({
                        severity: "success",
                        summary: "Success",
                        detail: response.data.message,
                        life: 3000,
                    });
                } else {
                    showToast({
                        severity: "error",
                        summary: "Error",
                        detail: response.data.message,
                        life: 3000,
                    });
                }
            })
            .catch((err: AxiosError) => {
                if (err.status === 401) {
                    dispatch(logout());
                    persistor.purge();
                    navigate("/admin/login");
                }
                errorHandler(err);
            }).finally(() => {
                setRejectDialogOpen(false);
                setRejectReason("");
                navigate("/admin/profiles");
            });
    }

    const fetchSurname = async (reset = false, selectId?: string) => {
        if (fetchingSurname) return;
        setFetchingSurname(true);
        const start = reset ? 0 : firstSurname;
        try {
            const response = await apiFunctions.get(
                `/surname/surnamedropdownlist/${start}/${rows}/${surnameSearchValue || selectId || ''}`
            );

            const newItems = response?.data?.data || [];
            const total = response?.data?.totalRecords || 0;

            if (reset) {
                setSurnameOption(newItems);
                setFirstSurname(rows);
            } else {
                setSurnameOption(prev => [...prev, ...newItems]);
                setFirstSurname(prev => prev + rows);
            }

            setSurnameTotalRecords(total);
        } finally {
            setFetchingSurname(false);
        }
    };

    useEffect(() => {
        // Only needed for the edit-mode dropdown — fetching it on every
        // plain profile VIEW added an unnecessary API call to the critical
        // path of just looking at a profile.
        if (!isEditMode) return;
        setFirstSurname(0);
        fetchSurname(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [surnameSearchValue, isEditMode]);

    const handleSurnameScroll = (e: any) => {
        const { scrollTop, offsetHeight, scrollHeight } = e.target;

        const nearBottom = scrollTop + offsetHeight >= scrollHeight - 10;
        const moreToLoad = surnameOption.length < surnameTotalRecords;

        if (nearBottom && moreToLoad && !fetchingSurname) {
            fetchSurname();
        }
    };

    const handleSurnameSearch = (value: any) => {
        setSurnameSearchValue(value);
    };

    const fetchSubcast = async (reset = false, selectId?: string) => {
        if (fetchingSurname) return;
        setFetchingSubcast(true);
        const start = reset ? 0 : firstSubcast;
        try {
            const response = await apiFunctions.get(
                `/subcast/subcastdropdownlist/${start}/${rows}/${surnameSearchValue || selectId || ''}`
            );

            const newItems = response?.data?.data || [];
            const total = response?.data?.totalRecords || 0;

            if (reset) {
                setSubcastOption(newItems);
                setFirstSubcast(rows);
            } else {
                setSubcastOption(prev => [...prev, ...newItems]);
                setFirstSubcast(prev => prev + rows);
            }

            setSubcastTotalRecords(total);
        } finally {
            setFetchingSubcast(false);
        }
    };

    useEffect(() => {
        // Same reasoning as the surname dropdown above — edit-mode only.
        if (!isEditMode) return;
        setFirstSubcast(0);
        fetchSubcast(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subcastSearchValue, isEditMode]);

    const handleSubcastScroll = (e: any) => {
        const { scrollTop, offsetHeight, scrollHeight } = e.target;

        const nearBottom = scrollTop + offsetHeight >= scrollHeight - 10;
        const moreToLoad = subcastOption.length < subcastTotalRecords;

        if (nearBottom && moreToLoad && !fetchingSubcast) {
            fetchSubcast();
        }
    };

    const handleSubcastSearch = (value: any) => {
        setSubcastSearchValue(value);
    };

    const handleImageUpload = async (file: File, fieldname: string) => {
        try {
            const formData = new FormData();
            formData.append("file", file);

            console.log(fieldname, "fielname");

            await apiFunctions.post(
                `/usermobile/mobile/updateadminimage/userprofile/${fieldname}?id=${id}`,
                formData,
                "",
                true
            );

            showToast({
                severity: "success",
                summary: "Success",
                detail: "Image updated successfully",
            });

            setIsEditMode(false);

            getUserData(); // refresh user data
        } catch (err) {
            console.error(err);
            showToast({
                severity: "error",
                summary: "Error",
                detail: "Image upload failed",
            });
        }
    };

    return (
        <>
            <div className="dashboard-margin">

                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                    {/* Title */}
                    <div className="w-full sm:w-40">
                        {dataLoad ? (
                            <Skeleton height="2rem" className="rounded" />
                        ) : (
                            <h2 className="card-title m-0">Profile</h2>
                        )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        {!userdata?.isapproved ? (
                            <>
                                <div className="w-full sm:w-36">
                                    {dataLoad ? (
                                        <Skeleton height="2.5rem" className="rounded" />
                                    ) : (
                                        <Button
                                            label="Approve User"
                                            className="w-full sm:w-auto"
                                            style={{ color: "#fff", background: "#008000" }}
                                            onClick={handleApprove}
                                        />
                                    )}
                                </div>

                                <div className="w-full sm:w-32">
                                    {dataLoad ? (
                                        <Skeleton height="2.5rem" className="rounded" />
                                    ) : (
                                        <Button
                                            label="Reject User"
                                            className="w-full sm:w-auto"
                                            style={{ color: "#fff", background: "#FF0000" }}
                                            onClick={() => setRejectDialogOpen(true)}
                                        />
                                    )}
                                </div>
                            </>
                        ) : (
                            <></>
                            // <div className="w-full sm:w-32">
                            //     {dataLoad ? (
                            //         <Skeleton height="2.5rem" className="rounded" />
                            //     ) : (
                            //         <Button
                            //             label="Report User"
                            //             className="w-full sm:w-auto"
                            //             style={{ color: "#fff", background: "#FF0000" }}
                            //             onClick={handleDeleteUser}
                            //         />
                            //     )}
                            // </div>
                        )}

                        <div className="w-full sm:w-40">
                            {dataLoad ? (
                                <Skeleton height="2.5rem" className="rounded" />
                            ) : (
                                <Button
                                    label="Send Message"
                                    outlined
                                    className="w-full sm:w-auto"
                                    style={{ color: "#264164", borderColor: "#264164" }}
                                    onClick={() => setMessageDialogOpen(true)}
                                />
                            )}
                        </div>

                        {userdata?.profiletype === "User" ? (
                            <div className="w-full sm:w-48">
                                {dataLoad ? (
                                    <Skeleton height="2.5rem" className="rounded" />
                                ) : (
                                    <Button
                                        label="Make Subadmin"
                                        className="w-full sm:w-auto"
                                        style={{ color: "#fff" }}
                                        onClick={() => handleProfileTypeChange("Subadmin")}
                                    />
                                )}
                            </div>
                        ) : (
                            <div className="w-full sm:w-32">
                                {dataLoad ? (
                                    <Skeleton height="2.5rem" className="rounded" />
                                ) : (
                                    <Button
                                        label="Make User"
                                        className="w-full sm:w-auto"
                                        style={{ color: "#fff" }}
                                        onClick={() => handleProfileTypeChange("User")}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <Card
                    className="w-100 h-auto mt-2"
                    header={
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 pt-4">
                            <h3 className="card-title m-0">Basic Info</h3>
                            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                                {isEditMode ? (
                                    <>
                                        <Button
                                            type="submit"
                                            form="basicInfoForm"
                                            label="Save"
                                            icon="pi pi-check"
                                            className="p-button-success p-button-sm w-full sm:w-auto"
                                        />
                                        <Button
                                            type="button"
                                            label="Cancel"
                                            icon="pi pi-times"
                                            className="p-button-secondary p-button-sm w-full sm:w-auto"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setIsEditMode(false);
                                            }}
                                        />
                                    </>
                                ) : (
                                    <Button
                                        type="button"
                                        label="Edit"
                                        icon={<PencilIcon className="h-4 w-4" />}
                                        className="p-button-sm w-full sm:w-auto flex items-center gap-2"
                                        onClick={(e) => {
                                            e.preventDefault();   // 🔥 stops form submit
                                            setIsEditMode(true);
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    }
                >
                    {dataLoad ? (
                        <div className="grid grid-cols-12 gap-4 mt-4">
                            <div className="col-span-12 md:col-span-8">
                                {Array.from({ length: 19 }).map((_, i) => (
                                    <RowSkeleton key={i} />
                                ))}
                                <div className="flex flex-col md:flex-row items-start md:items-center gap-6 mt-4">
                                    <Skeleton shape="circle" size="100px" />
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
                                        {Array.from({ length: 4 }).map((_, i) => (
                                            <ChecklistSkeleton key={i} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="col-span-12 md:col-span-4 flex flex-col items-center gap-2">
                                <Skeleton height="200px" width="200px" className="rounded" />
                                <Skeleton height="200px" width="200px" className="rounded" />
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-12 gap-4 mt-4">
                                <div className="col-span-12 md:col-span-8">
                                    <form id="basicInfoForm" onSubmit={formik.handleSubmit}>
                                        <div className="flex flex-col lg:flex-row gap-2 mt-1">
                                            <span className="font-bold min-w-[200px]">Name:</span>
                                            {!isEditMode && (
                                                <div>
                                                    {userdata?.fullname || "-"}
                                                    <span style={{ margin: "0 0.5rem" }}>-</span>
                                                    {userdata?.fullnamegujarati || "-"}
                                                </div>
                                            )}
                                            {isEditMode && (
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                    <div className="flex flex-col gap-1">
                                                        <label className="text-sm font-medium text-gray-700">Honorifics</label>
                                                        <div className="flex flex-col gap-2">
                                                            <InputText
                                                                name="honorifics"
                                                                placeholder="Honorifics"
                                                                value={formik.values.honorifics}
                                                                onChange={formik.handleChange}
                                                                className={`${formik.touched.honorifics && formik.errors.honorifics ? "p-invalid" : ""}`}
                                                            />
                                                            {formik.touched.honorifics && formik.errors.honorifics && (
                                                                <small className="p-error">{formik.errors.honorifics}</small>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col gap-1">
                                                        <label className="text-sm font-medium text-gray-700">Surname</label>
                                                        <div className="flex flex-col gap-2">
                                                            <Select
                                                                virtual={false}
                                                                allowClear
                                                                showSearch
                                                                placeholder=""
                                                                className={`w-full sm:w-72 ${formik.touched.surnameid && formik.errors.surnameid ? "border-red-500" : ""
                                                                    }`}
                                                                style={{ height: "42px" }}
                                                                filterOption={false}
                                                                onSearch={handleSurnameSearch}
                                                                onPopupScroll={handleSurnameScroll}
                                                                notFoundContent={fetchingSurname ? <Spin size="small" /> : "No data"}
                                                                value={formik.values.surnameid}
                                                                onClear={() => {
                                                                    formik.setFieldValue("surnameid", null);
                                                                }}
                                                                onChange={(value) => {
                                                                    formik.setFieldValue("surnameid", value);
                                                                }}
                                                            >
                                                                {surnameOption.map((subcast) => (
                                                                    <Option key={subcast.id} value={subcast.id}>
                                                                        {subcast.name}
                                                                    </Option>
                                                                ))}
                                                            </Select>
                                                            {formik.touched.surnameid && formik.errors.surnameid && (
                                                                <small className="p-error">{formik.errors.surnameid}</small>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col gap-1">
                                                        <label className="text-sm font-medium text-gray-700">First Name</label>
                                                        <div className="flex flex-col gap-2">
                                                            <InputText
                                                                name="name"
                                                                placeholder="First Name"
                                                                value={formik.values.name}
                                                                onChange={formik.handleChange}
                                                                className={`${formik.touched.name && formik.errors.name ? "p-invalid" : ""}`}
                                                            />
                                                            {formik.touched.name && formik.errors.name && (
                                                                <small className="p-error">{formik.errors.name}</small>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col gap-1">
                                                        <label className="text-sm font-medium text-gray-700">
                                                            First Name (Gujarati)
                                                        </label>
                                                        <div className="flex flex-col gap-2">
                                                            <InputText
                                                                name="namegujarati"
                                                                placeholder="First Name (Gujarati)"
                                                                value={formik.values.namegujarati}
                                                                onChange={formik.handleChange}
                                                                className={`${formik.touched.namegujarati && formik.errors.namegujarati ? "p-invalid" : ""}`}
                                                            />
                                                            {formik.touched.namegujarati && formik.errors.namegujarati && (
                                                                <small className="p-error">{formik.errors.namegujarati}</small>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col gap-1">
                                                        <label className="text-sm font-medium text-gray-700">Father Name</label>
                                                        <div className="flex flex-col gap-2">
                                                            <InputText
                                                                name="fathername"
                                                                placeholder="Father Name"
                                                                value={formik.values.fathername}
                                                                onChange={formik.handleChange}
                                                                className={`${formik.touched.fathername && formik.errors.fathername ? "p-invalid" : ""}`}
                                                            />
                                                            {formik.touched.fathername && formik.errors.fathername && (
                                                                <small className="p-error">{formik.errors.fathername}</small>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col gap-1">
                                                        <label className="text-sm font-medium text-gray-700">
                                                            Father Name (Gujarati)
                                                        </label>
                                                        <div className="flex flex-col gap-2">
                                                            <InputText
                                                                name="fathernamegujarati"
                                                                placeholder="Father Name (Gujarati)"
                                                                value={formik.values.fathernamegujarati}
                                                                onChange={formik.handleChange}
                                                                className={`${formik.touched.fathernamegujarati && formik.errors.fathernamegujarati ? "p-invalid" : ""}`}
                                                            />
                                                            {formik.touched.fathernamegujarati && formik.errors.fathernamegujarati && (
                                                                <small className="p-error">{formik.errors.fathernamegujarati}</small>
                                                            )}
                                                        </div>
                                                    </div>

                                                </div>
                                            )}
                                        </div>
                                        <div className={`flex flex-col sm:flex-row gap-2 ${!isEditMode ? "mt-2" : "mt-4"}`}>
                                            <span className="font-bold min-w-[200px]">Sub Cast:</span>
                                            {!isEditMode ? (
                                                <span>{userdata?.subcastvalue || "-"}</span>
                                            ) : (
                                                <div className="flex flex-col gap-2">
                                                    <Select
                                                        virtual={false}
                                                        allowClear
                                                        showSearch
                                                        placeholder=""
                                                        className={`w-full sm:w-72 ${formik.touched.subcastid && formik.errors.subcastid ? "border-red-500" : ""
                                                            }`}
                                                        style={{ height: "42px" }}
                                                        filterOption={false}
                                                        onSearch={handleSubcastSearch}
                                                        onPopupScroll={handleSubcastScroll}
                                                        notFoundContent={fetchingSubcast ? <Spin size="small" /> : "No data"}
                                                        value={formik.values.subcastid}
                                                        onClear={() => {
                                                            formik.setFieldValue("subcastid", null);
                                                        }}
                                                        onChange={(value) => {
                                                            formik.setFieldValue("subcastid", value);
                                                        }}
                                                    >
                                                        {subcastOption.map((subcast) => (
                                                            <Option key={subcast.id} value={subcast.id}>
                                                                {subcast.name}
                                                            </Option>
                                                        ))}
                                                    </Select>
                                                    {formik.touched.subcastid && formik.errors.subcastid && (
                                                        <small className="p-error">{formik.errors.subcastid}</small>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className={`flex flex-col sm:flex-row gap-2 ${!isEditMode ? "mt-2" : "mt-3"}`}>
                                            <span className="font-bold min-w-[200px]">User Profile:</span>
                                            {!isEditMode ? (
                                                <span>{userdata?.profiletype}</span>
                                            ) : (
                                                <>
                                                    <div className="flex flex-col gap-2">
                                                        <Dropdown
                                                            value={formik.values.profiletype}
                                                            options={profileTypeOptions}
                                                            optionLabel="label"
                                                            optionValue="value"
                                                            onChange={(e) => formik.setFieldValue("profiletype", e.value)}
                                                            className={`w-full sm:w-72 ${formik.touched.profiletype && formik.errors.profiletype
                                                                ? "p-invalid"
                                                                : ""
                                                                }`}
                                                        />
                                                        {formik.touched.profiletype && formik.errors.profiletype && (
                                                            <small className="p-error">{formik.errors.profiletype}</small>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        <div className={`flex flex-col sm:flex-row gap-2 ${!isEditMode ? "mt-2" : "mt-4"}`}>
                                            <span className="font-bold min-w-[200px]">Gender:</span>
                                            {!isEditMode ? (
                                                <span>{userdata?.gender}</span>
                                            ) : (
                                                <>
                                                    <div className="flex flex-col gap-2">
                                                        <Dropdown
                                                            value={formik.values.gender}
                                                            options={genderOptions}
                                                            optionLabel="label"
                                                            optionValue="value"
                                                            onChange={(e) => formik.setFieldValue("gender", e.value)}
                                                            className={`w-full sm:w-72 ${formik.touched.gender && formik.errors.gender
                                                                ? "p-invalid"
                                                                : ""
                                                                }`}
                                                        />
                                                        {formik.touched.gender && formik.errors.gender && (
                                                            <small className="p-error">{formik.errors.gender}</small>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        <div className={`flex flex-col sm:flex-row gap-2 ${isEditMode ? "mt-2" : "mt-4"}`}>
                                            <span className="font-bold min-w-[200px]">Date of Birth:</span>
                                            {!isEditMode ? (
                                                <span>{userdata?.dobfull}</span>
                                            ) : (
                                                <>
                                                    <div className="flex flex-col gap-2">
                                                        <Calendar
                                                            value={formik.values.dob ? new Date(formik.values.dob) : null}
                                                            onChange={(e) => formik.setFieldValue("dob", e.value)}
                                                            onBlur={() => formik.setFieldTouched("dob", true)}
                                                            showIcon
                                                            className={`w-full sm:w-72 ${formik.touched.dob && formik.errors.dob ? "p-invalid" : ""
                                                                }`}
                                                        />
                                                        {formik.touched.dob && formik.errors.dob && (
                                                            <small className="p-error">{formik.errors.dob}</small>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        <div className={`flex flex-col sm:flex-row gap-2  ${isEditMode ? "mt-2" : "mt-4"}`}>
                                            <span className="font-bold min-w-[200px]">Marital Status:</span>
                                            {!isEditMode ? (
                                                <span>{userdata?.maritalstatus}</span>
                                            ) : (
                                                <div className="flex flex-col gap-2">
                                                    <Dropdown
                                                        value={formik.values.maritalstatus}
                                                        options={maritalStatusOptions}
                                                        optionLabel="label"
                                                        optionValue="value"
                                                        onChange={(e) => formik.setFieldValue("maritalstatus", e.value)}
                                                        className={`w-full sm:w-72 ${formik.touched.maritalstatus && formik.errors.maritalstatus
                                                            ? "p-invalid"
                                                            : ""
                                                            }`}
                                                    />
                                                    {formik.touched.maritalstatus && formik.errors.maritalstatus && (
                                                        <small className="p-error">{formik.errors.maritalstatus}</small>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className={`flex flex-col sm:flex-row gap-2 ${!isEditMode ? "mt-2" : "mt-4"}`}>
                                            <span className="font-bold min-w-[200px]">Date of Anniversary:</span>
                                            {!isEditMode ? (
                                                <span>{userdata?.doafull || "-"}</span>
                                            ) : (
                                                <div className="flex flex-col gap-2">
                                                    <Calendar
                                                        value={formik.values.doa ? new Date(formik.values.doa) : null}
                                                        onChange={(e) => formik.setFieldValue("doa", e.value)}
                                                        onBlur={() => formik.setFieldTouched("doa", true)}
                                                        showIcon
                                                        className={`w-full sm:w-72 ${formik.touched.doa && formik.errors.doa ? "p-invalid" : ""
                                                            }`}
                                                    />
                                                    {formik.touched.doa && formik.errors.doa && (
                                                        <small className="p-error">{formik.errors.doa}</small>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className={`flex flex-col sm:flex-row gap-2 ${!isEditMode ? "mt-2" : "mt-4"}`}>
                                            <span className="font-bold min-w-[200px]">Blood Group:</span>
                                            {!isEditMode ? (
                                                <span>{userdata?.bloodgroup || "-"}</span>
                                            ) : (
                                                <>
                                                    <div className="flex flex-col gap-2">
                                                        <Dropdown
                                                            value={formik.values.bloodgroup}
                                                            options={bloodGroupOptions}
                                                            optionLabel="label"
                                                            optionValue="value"
                                                            onChange={(e) => formik.setFieldValue("bloodgroup", e.value)}
                                                            className={`w-full sm:w-72 ${formik.touched.bloodgroup && formik.errors.bloodgroup
                                                                ? "p-invalid"
                                                                : ""
                                                                }`}
                                                        />
                                                        {formik.touched.bloodgroup && formik.errors.bloodgroup && (
                                                            <small className="p-error">{formik.errors.bloodgroup}</small>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        <div className={`flex flex-col sm:flex-row gap-2 ${!isEditMode ? "mt-2" : "mt-4"}`}>
                                            <span className="font-bold min-w-[200px]">Is Alive:</span>
                                            {!isEditMode ? (
                                                <span>{userdata?.isalive ? "Yes" : "No"}</span>
                                            ) : (
                                                <>
                                                    <div className="flex flex-col gap-2">
                                                        <Dropdown
                                                            value={formik.values.isalive}
                                                            options={[
                                                                { label: "Yes", value: true },
                                                                { label: "No", value: false },
                                                            ]}
                                                            optionLabel="label"
                                                            optionValue="value"
                                                            onChange={(e) => formik.setFieldValue("isalive", e.value)}
                                                            className={`w-full sm:w-72 ${formik.touched.isalive && formik.errors.isalive
                                                                ? "p-invalid"
                                                                : ""
                                                                }`}
                                                        />
                                                        {formik.touched.isalive && formik.errors.isalive && (
                                                            <small className="p-error">{formik.errors.isalive}</small>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        <div className={`flex flex-col sm:flex-row gap-2 ${~isEditMode ? "mt-2" : "mt-4"}`}>
                                            <span className="font-bold min-w-[200px]">
                                                Date of Death
                                            </span>
                                            {!isEditMode ? (
                                                <span>{userdata?.dod || "-"}</span>
                                            ) : (
                                                <div className="flex flex-col gap-2">
                                                    <Calendar
                                                        value={formik.values.dod ? new Date(formik.values.dod) : null}
                                                        onChange={(e) => formik.setFieldValue("dod", e.value)}
                                                        onBlur={() => formik.setFieldTouched("dod", true)}
                                                        showIcon
                                                        className={`w-full sm:w-72 ${formik.touched.dod && formik.errors.dod ? "p-invalid" : ""
                                                            }`}
                                                    />
                                                    {formik.touched.dod && formik.errors.dod && (
                                                        <small className="p-error">{formik.errors.dod}</small>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className={`flex flex-col lg:flex-row gap-2 ${!isEditMode ? "mt-2" : "mt-4"}`}>
                                            <span className="font-bold min-w-[200px]">Native Village / City:</span>
                                            {!isEditMode && (
                                                <div>
                                                    <div>{userdata?.nativeLocation || "-"}</div>
                                                    {userdata?.nativecity && (
                                                        <div className="text-sm text-gray-500 mt-1">{userdata.nativecity}</div>
                                                    )}
                                                </div>
                                            )}
                                            {isEditMode && (
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                    <div className="flex flex-col gap-1 sm:col-span-2">
                                                        <label className="text-sm font-medium text-gray-700">Search Native Location (Google Maps)</label>
                                                        <input
                                                            ref={nativePlaceInputRef}
                                                            type="text"
                                                            defaultValue={formik.values.nativeaddress}
                                                            placeholder="Type to search place..."
                                                            className="p-inputtext p-component w-full"
                                                            style={{ padding: '0.5rem', border: '1px solid #ced4da', borderRadius: '4px', width: '100%' }}
                                                        />
                                                        {formik.values.nativecity && (
                                                            <small className="text-gray-500">City: {formik.values.nativecity}</small>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className={`flex flex-col lg:flex-row gap-2 ${!isEditMode ? "mt-2" : "mt-4"}`}>
                                            <span className="font-bold min-w-[200px]">Current Village / City:</span>
                                            {!isEditMode && (
                                                <div>
                                                    {userdata?.currentLocation ? (
                                                        <>
                                                            <div>{userdata.currentLocation}</div>
                                                            {userdata?.currentcity && (
                                                                <div className="text-sm text-gray-500 mt-1">{userdata.currentcity}</div>
                                                            )}
                                                        </>
                                                    ) : userdata?.tempcity ? (
                                                        // No real current address yet — fall back to the IP-based
                                                        // city estimate. Swaps out automatically once they add a real one.
                                                        <div>{userdata.tempcity} <span className="text-sm text-gray-500">(approximate)</span></div>
                                                    ) : (
                                                        <div>-</div>
                                                    )}
                                                </div>
                                            )}
                                            {isEditMode && (
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                    <div className="flex flex-col gap-1 sm:col-span-2">
                                                        <label className="text-sm font-medium text-gray-700">Search Current Location (Google Maps)</label>
                                                        <input
                                                            ref={currentPlaceInputRef}
                                                            type="text"
                                                            defaultValue={formik.values.currentaddress}
                                                            placeholder="Type to search place..."
                                                            className="p-inputtext p-component w-full"
                                                            style={{ padding: '0.5rem', border: '1px solid #ced4da', borderRadius: '4px', width: '100%' }}
                                                        />
                                                        {formik.values.currentcity && (
                                                            <small className="text-gray-500">City: {formik.values.currentcity}</small>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        {!isEditMode && userdata?.currentlat != null && userdata?.currentlng != null && (
                                            <CollapsibleSection title="Current Location Map">
                                                <LocationMap
                                                    title="Current Location"
                                                    loading={false}
                                                    locations={[{
                                                        city: userdata.currentcity || "Current Location",
                                                        count: 1,
                                                        lat: userdata.currentlat,
                                                        lng: userdata.currentlng,
                                                    }] as LocationPoint[]}
                                                    subtitle={userdata.currentLocation || userdata.currentcity || ""}
                                                    unitLabel="pin"
                                                    showCityList={false}
                                                    maxFitZoom={14}
                                                />
                                            </CollapsibleSection>
                                        )}
                                        {/* Fallback only — once the user adds a real current address, that
                                            map (above) takes over and this IP-based estimate no longer shows. */}
                                        {!isEditMode && userdata?.currentlat == null && userdata?.templat != null && userdata?.templng != null && (
                                            <CollapsibleSection title="Current Village/City Location Map">
                                                <LocationMap
                                                    title="Current Village/City Location (Approximate)"
                                                    loading={false}
                                                    locations={[{
                                                        city: userdata.templocation || "Approximate Location",
                                                        count: 1,
                                                        lat: userdata.templat,
                                                        lng: userdata.templng,
                                                    }] as LocationPoint[]}
                                                    subtitle={userdata.templocation || ""}
                                                    unitLabel="pin"
                                                    showCityList={false}
                                                    maxFitZoom={10}
                                                />
                                            </CollapsibleSection>
                                        )}
                                        {!isEditMode && (userdata?.ip || userdata?.templocation) && (
                                            <div className="flex flex-col lg:flex-row gap-2 mt-2">
                                                <span className="font-bold min-w-[200px]">IP Address:</span>
                                                <div>
                                                    <div className="flex items-center">
                                                        <CountryFlag countryCode={userdata?.ipcountry} />
                                                        {userdata?.ip || "-"}
                                                    </div>
                                                    {userdata?.templocation && (
                                                        <div className="text-sm text-gray-500 mt-1">{userdata.templocation}</div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        {/* Where/when this user was last seen active anywhere in the app —
                                            separate from Current/IP-based location above, since it reflects
                                            the active-log's own geocoded ip, not the registered address. */}
                                        {!isEditMode && userdata?.lastactiveago && (
                                            <div className="flex flex-col lg:flex-row gap-2 mt-2">
                                                <span className="font-bold min-w-[200px]">Last Active:</span>
                                                <div>
                                                    <div>
                                                        {userdata.lastactiveago}
                                                        {userdata?.lastactivecity ? ` · ${userdata.lastactivecity}` : ""}
                                                    </div>
                                                    {userdata?.lastactiveip && (
                                                        <div className="text-sm text-gray-500 mt-1 flex items-center">
                                                            IP:&nbsp;<CountryFlag countryCode={userdata?.lastactiveipcountry} />{userdata.lastactiveip}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        {!isEditMode && userdata?.lastactivelat != null && userdata?.lastactivelng != null && (
                                            <CollapsibleSection title="Last Active Location Map">
                                                <LocationMap
                                                    title="Last Active Location"
                                                    loading={false}
                                                    locations={[{
                                                        city: userdata.lastactivecity || "Last Active Location",
                                                        count: 1,
                                                        lat: userdata.lastactivelat,
                                                        lng: userdata.lastactivelng,
                                                    }] as LocationPoint[]}
                                                    subtitle={userdata.lastactiveago ? `${userdata.lastactiveago}${userdata?.lastactivecity ? ` · ${userdata.lastactivecity}` : ""}` : (userdata.lastactivecity || "")}
                                                    unitLabel="pin"
                                                    showCityList={false}
                                                    maxFitZoom={10}
                                                />
                                            </CollapsibleSection>
                                        )}
                                        {!isEditMode && (
                                            <CollapsibleSection title="Notification History" onOpen={fetchNotificationHistory}>
                                                {notificationHistoryLoading ? (
                                                    <div style={{ padding: "12px 0" }}>Loading...</div>
                                                ) : notificationHistory.length === 0 ? (
                                                    <div style={{ padding: "12px 0", color: "#6b7280" }}>No notifications sent to this user yet.</div>
                                                ) : (
                                                    <div className="flex flex-col gap-2" style={{ maxHeight: "320px", overflowY: "auto" }}>
                                                        {notificationHistory.map((log: any) => (
                                                            <div key={log._id} className="flex items-start justify-between gap-2" style={{ padding: "8px 0", borderBottom: "1px solid #e5e7eb" }}>
                                                                <div style={{ minWidth: 0, flex: 1 }}>
                                                                    <div className="flex items-center gap-2">
                                                                        <span
                                                                            className="px-2 py-0.5 rounded text-xs font-semibold"
                                                                            style={{
                                                                                background: log.channel === "whatsapp" ? "#dcfce7" : "#dbeafe",
                                                                                color: log.channel === "whatsapp" ? "#15803d" : "#1d4ed8",
                                                                            }}
                                                                        >
                                                                            {log.channel === "whatsapp" ? "WhatsApp" : "Push"}
                                                                        </span>
                                                                        <span style={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{log.title}</span>
                                                                    </div>
                                                                    <p className="m-0" style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>{log.message}</p>
                                                                    <p className="m-0" style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>
                                                                        {new Date(log.createddate).toLocaleString("en-IN")}
                                                                    </p>
                                                                </div>
                                                                <span
                                                                    className="px-2 py-0.5 rounded text-xs font-semibold"
                                                                    style={{
                                                                        background: log.send_status === "sent" ? "#dcfce7" : log.send_status === "failed" ? "#fee2e2" : "#fef9c3",
                                                                        color: log.send_status === "sent" ? "#15803d" : log.send_status === "failed" ? "#b91c1c" : "#a16207",
                                                                        flexShrink: 0,
                                                                    }}
                                                                >
                                                                    {log.send_status}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </CollapsibleSection>
                                        )}
                                        {!isEditMode && (
                                            <CollapsibleSection title="Chats" onOpen={fetchChatConversations}>
                                                {/* Global search — separate from the two lists below: finds users AND
                                                    messages across the whole platform, not just this user's chats.
                                                    Hidden while loading/empty — nothing meaningful to search yet. */}
                                                {!chatConversationsLoading && chatConversations.length > 0 && (
                                                <div ref={chatGlobalSearchRef} style={{ position: "relative", marginBottom: "10px" }}>
                                                    <div className="flex items-center" style={{ background: "#f3f4f6", borderRadius: "8px", padding: "8px 10px", gap: "8px" }}>
                                                        <MagnifyingGlassIcon className="w-4 h-4" style={{ color: "#9ca3af" }} />
                                                        <input
                                                            type="text"
                                                            placeholder="Search all users and messages..."
                                                            value={chatGlobalQuery}
                                                            onChange={(e) => { setChatGlobalQuery(e.target.value); setChatGlobalOpen(true); }}
                                                            onFocus={() => setChatGlobalOpen(true)}
                                                            style={{ border: "none", outline: "none", background: "transparent", width: "100%", fontSize: "13px" }}
                                                        />
                                                    </div>
                                                    {chatGlobalOpen && chatGlobalQuery.trim() && (
                                                        <div style={{
                                                            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 20,
                                                            background: "#fff", borderRadius: "8px", boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                                                            maxHeight: "300px", overflowY: "auto", border: "1px solid #e5e7eb",
                                                        }}>
                                                            {chatGlobalLoading ? (
                                                                <div style={{ padding: "12px", fontSize: "12px", color: "#6b7280" }}>Searching...</div>
                                                            ) : chatGlobalUsers.length === 0 && chatGlobalMessages.length === 0 ? (
                                                                <div style={{ padding: "12px", fontSize: "12px", color: "#6b7280" }}>No users or messages match "{chatGlobalQuery}".</div>
                                                            ) : (
                                                                <>
                                                                    {chatGlobalUsers.length > 0 && (
                                                                        <div>
                                                                            <div style={{ padding: "6px 12px", fontSize: "10px", fontWeight: 700, color: "#6b7280", background: "#f9fafb" }}>USERS</div>
                                                                            {chatGlobalUsers.map((u) => (
                                                                                <div
                                                                                    key={u.id}
                                                                                    onClick={() => goToUserProfileFromChat(u.id)}
                                                                                    className="flex items-center gap-2 cursor-pointer"
                                                                                    style={{ padding: "6px 12px", borderTop: "1px solid #f3f4f6" }}
                                                                                >
                                                                                    <img src={u.profileimg} alt={u.name} style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover" }} />
                                                                                    <div style={{ minWidth: 0 }}>
                                                                                        <p className="m-0" style={{ fontSize: "12px", fontWeight: 600, color: "#111827" }}>{u.name}</p>
                                                                                        <p className="m-0" style={{ fontSize: "10px", color: "#6b7280" }}>{u.mobileno}</p>
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                    {chatGlobalMessages.length > 0 && (
                                                                        <div>
                                                                            <div style={{ padding: "6px 12px", fontSize: "10px", fontWeight: 700, color: "#6b7280", background: "#f9fafb" }}>MESSAGES</div>
                                                                            {chatGlobalMessages.map((m) => (
                                                                                <div
                                                                                    key={m._id}
                                                                                    onClick={() => goToGlobalChatMessage(m)}
                                                                                    className="flex items-center gap-2 cursor-pointer"
                                                                                    style={{ padding: "6px 12px", borderTop: "1px solid #f3f4f6" }}
                                                                                >
                                                                                    <img src={m.sender.profileimg} alt={m.sender.name} style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                                                                                    <div style={{ minWidth: 0, flex: 1 }}>
                                                                                        <p className="m-0" style={{ fontSize: "11px", fontWeight: 600, color: "#111827" }}>
                                                                                            {m.sender.name} → {m.receiver.name}
                                                                                        </p>
                                                                                        <p className="m-0" style={{ fontSize: "11px", color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                                            {m.preview}
                                                                                        </p>
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                )}
                                                {chatConversationsLoading ? (
                                                    <div style={{ padding: "12px 0" }}>Loading...</div>
                                                ) : chatConversations.length === 0 ? (
                                                    <div className="flex flex-col items-center justify-center" style={{ padding: "24px 0", color: "#6b7280", textAlign: "center" }}>
                                                        <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "10px" }}>
                                                            <ChatBubbleLeftRightIcon className="w-6 h-6" style={{ color: "#9ca3af" }} />
                                                        </div>
                                                        <span style={{ fontSize: "13px" }}>This user hasn't chatted with anyone yet.</span>
                                                    </div>
                                                ) : (
                                                    <div className="flex" style={{ height: "420px", border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden" }}>
                                                        {/* Conversation list — left */}
                                                        <div className="flex flex-col" style={{ width: "38%", minWidth: "180px", borderRight: "1px solid #e5e7eb" }}>
                                                            <div style={{ padding: "8px" }}>
                                                                <input
                                                                    type="text"
                                                                    placeholder="Search chats..."
                                                                    value={chatConversationSearch}
                                                                    onChange={(e) => setChatConversationSearch(e.target.value)}
                                                                    style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "6px 8px", fontSize: "12px", outline: "none" }}
                                                                />
                                                            </div>
                                                            <div style={{ flex: 1, overflowY: "auto" }}>
                                                                {visibleChatConversations.length === 0 ? (
                                                                    <div style={{ padding: "12px", fontSize: "12px", color: "#6b7280" }}>No matches.</div>
                                                                ) : visibleChatConversations.map((c: any) => {
                                                                    const isActive = chatThreadPartner?.partner_id === c.partner_id;
                                                                    return (
                                                                        <div
                                                                            key={c.partner_id}
                                                                            onClick={() => openChatThread(c)}
                                                                            className="flex items-center gap-2 cursor-pointer"
                                                                            style={{
                                                                                padding: "10px 12px",
                                                                                borderBottom: "1px solid #e5e7eb",
                                                                                background: isActive ? "#eff6ff" : "transparent",
                                                                            }}
                                                                        >
                                                                            <img
                                                                                src={c.profileimg}
                                                                                alt={c.name}
                                                                                style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0, background: "#f3f4f6" }}
                                                                            />
                                                                            <div style={{ minWidth: 0, flex: 1 }}>
                                                                                <p className="m-0" style={{ fontSize: "13px", fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                                    {c.name} {c.surname}
                                                                                </p>
                                                                                <p className="m-0" style={{ fontSize: "11px", color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                                    {c.lastMessage?.sender_id === id ? "You: " : ""}{c.lastMessage?.preview}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>

                                                        {/* Thread — right */}
                                                        <div className="flex flex-col" style={{ flex: 1, minWidth: 0 }}>
                                                            {!chatThreadPartner ? (
                                                                <div className="flex flex-col items-center justify-center" style={{ flex: 1, color: "#6b7280", padding: "16px", textAlign: "center" }}>
                                                                    <p className="m-0" style={{ fontWeight: 600, color: "#111827" }}>Select a Conversation</p>
                                                                    <p className="m-0" style={{ fontSize: "12px", marginTop: "4px" }}>Choose a chat from the left to view the messages.</p>
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <div className="flex items-center justify-between" style={{ padding: "10px 12px", borderBottom: "1px solid #e5e7eb" }}>
                                                                        <span style={{ fontWeight: 600, fontSize: "13px", color: "#111827" }}>
                                                                            {chatThreadPartner.name} {chatThreadPartner.surname}
                                                                        </span>
                                                                        <div className="flex items-center gap-1">
                                                                            <button
                                                                                onClick={() => {
                                                                                    const opening = !chatThreadSearchOpen;
                                                                                    setChatThreadSearchOpen(opening);
                                                                                    if (!opening) { setChatThreadSearchQuery(""); setChatThreadType(""); }
                                                                                }}
                                                                                style={{
                                                                                    width: 26, height: 26, borderRadius: "50%", border: "none", cursor: "pointer",
                                                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                                                    background: chatThreadSearchOpen ? "#eff6ff" : "transparent",
                                                                                    color: chatThreadSearchOpen ? "#1d4ed8" : "#6b7280",
                                                                                }}
                                                                            >
                                                                                🔍
                                                                            </button>
                                                                            <button
                                                                                onClick={deleteChatConversation}
                                                                                title="Delete entire conversation"
                                                                                style={{
                                                                                    width: 26, height: 26, borderRadius: "50%", border: "none", cursor: "pointer",
                                                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                                                    background: "transparent", color: "#ef4444",
                                                                                }}
                                                                            >
                                                                                <TrashIcon className="w-4 h-4" />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                    {chatThreadSearchOpen && (
                                                                        <div style={{ padding: "8px", borderBottom: "1px solid #e5e7eb", display: "flex", flexDirection: "column", gap: "6px" }}>
                                                                            <input
                                                                                type="text"
                                                                                autoFocus
                                                                                placeholder="Search within this chat..."
                                                                                value={chatThreadSearchQuery}
                                                                                onChange={(e) => setChatThreadSearchQuery(e.target.value)}
                                                                                style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "6px 8px", fontSize: "12px", outline: "none" }}
                                                                            />
                                                                            <div className="flex items-center gap-1" style={{ flexWrap: "wrap" }}>
                                                                                {CHAT_TYPE_TABS.map((tab) => (
                                                                                    <button
                                                                                        key={tab.value}
                                                                                        onClick={() => setChatThreadType(tab.value)}
                                                                                        style={{
                                                                                            border: "none", borderRadius: "999px", padding: "3px 10px", fontSize: "10px", fontWeight: 600, cursor: "pointer",
                                                                                            background: chatThreadType === tab.value ? "#1d4ed8" : "#f3f4f6",
                                                                                            color: chatThreadType === tab.value ? "#fff" : "#374151",
                                                                                        }}
                                                                                    >
                                                                                        {tab.label}
                                                                                    </button>
                                                                                ))}
                                                                            </div>
                                                                            {(chatThreadSearchQuery.trim() || chatThreadType) && (
                                                                                <div style={{ maxHeight: "180px", overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: "6px" }}>
                                                                                    {chatSearchResultsLoading ? (
                                                                                        <div style={{ padding: "8px", fontSize: "11px", color: "#6b7280" }}>Searching...</div>
                                                                                    ) : chatSearchResults.length === 0 ? (
                                                                                        <div style={{ padding: "8px", fontSize: "11px", color: "#6b7280" }}>No matches found.</div>
                                                                                    ) : (
                                                                                        groupedChatSearchResults.map((group, gi) => (
                                                                                            <div key={gi}>
                                                                                                <div style={{ padding: "3px 8px", background: "#f9fafb", fontSize: "9px", fontWeight: 700, color: "#6b7280" }}>
                                                                                                    {group.label.toUpperCase()}
                                                                                                </div>
                                                                                                {group.items.map((m: any) => (
                                                                                                    <div
                                                                                                        key={m._id}
                                                                                                        onClick={() => jumpToChatMessage(chatThreadPartner, m._id)}
                                                                                                        className="cursor-pointer"
                                                                                                        style={{ padding: "6px 8px", borderTop: "1px solid #f3f4f6" }}
                                                                                                    >
                                                                                                        <p className="m-0" style={{ fontSize: "11px", color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                                                            {chatResultPreview(m)}
                                                                                                        </p>
                                                                                                    </div>
                                                                                                ))}
                                                                                            </div>
                                                                                        ))
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                    <div
                                                                        ref={chatMessagesContainerRef}
                                                                        onScroll={handleChatThreadScroll}
                                                                        onLoadCapture={handleChatThreadMediaLoad}
                                                                        onLoadedDataCapture={handleChatThreadMediaLoad}
                                                                        className="flex flex-col gap-2"
                                                                        style={{ flex: 1, overflowY: "auto", padding: "10px" }}
                                                                    >
                                                                        {chatLoadingMore && (
                                                                            <div className="flex justify-center">
                                                                                <span style={{ fontSize: "10px", fontWeight: 600, color: "#9ca3af" }}>Loading older messages...</span>
                                                                            </div>
                                                                        )}
                                                                        {chatMessagesLoading ? (
                                                                            <div style={{ textAlign: "center", padding: "12px 0" }}>Loading...</div>
                                                                        ) : chatMessages.length === 0 ? (
                                                                            <div className="flex flex-col items-center justify-center" style={{ padding: "20px 0", color: "#6b7280", textAlign: "center" }}>
                                                                                <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "8px" }}>
                                                                                    <ChatBubbleLeftRightIcon className="w-5 h-5" style={{ color: "#9ca3af" }} />
                                                                                </div>
                                                                                <span style={{ fontSize: "12px" }}>No messages in this conversation.</span>
                                                                            </div>
                                                                        ) : (
                                                                            [...chatMessages].reverse().map((m: any) => {
                                                                                const isFromProfileUser = m.sender_id === id;
                                                                                const isHighlighted = m._id === chatHighlightedMessageId;
                                                                                return (
                                                                                    <div
                                                                                        key={m._id}
                                                                                        className="group"
                                                                                        style={{ alignSelf: isFromProfileUser ? "flex-start" : "flex-end", maxWidth: "80%" }}
                                                                                    >
                                                                                    <div
                                                                                        ref={(el) => { chatMessageRefs.current[m._id] = el; }}
                                                                                        style={{
                                                                                            position: "relative",
                                                                                            background: isHighlighted ? "#fef08a" : (isFromProfileUser ? "#f3f4f6" : "#dbeafe"),
                                                                                            borderRadius: "10px",
                                                                                            padding: "8px 10px",
                                                                                            transition: "background 0.4s ease",
                                                                                        }}
                                                                                    >
                                                                                        <button
                                                                                            onClick={() => deleteChatMessage(m._id)}
                                                                                            title="Delete message"
                                                                                            className="opacity-0 group-hover:opacity-100"
                                                                                            style={{
                                                                                                position: "absolute", top: -8, [isFromProfileUser ? "right" : "left"]: -8,
                                                                                                width: 20, height: 20, borderRadius: "50%", border: "1px solid #e5e7eb",
                                                                                                background: "#fff", color: "#ef4444", cursor: "pointer",
                                                                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                                                                boxShadow: "0 1px 3px rgba(0,0,0,0.15)", transition: "opacity 0.15s",
                                                                                            }}
                                                                                        >
                                                                                            <TrashIcon style={{ width: 11, height: 11 }} />
                                                                                        </button>
                                                                                        <ChatMessageContent message={m} searchQuery={chatThreadSearchQuery.trim()} />
                                                                                        <p className="m-0" style={{ fontSize: "10px", color: "#9ca3af", marginTop: "4px" }}>
                                                                                            {new Date(m.createddate).toLocaleString("en-IN")}
                                                                                        </p>
                                                                                    </div>
                                                                                    </div>
                                                                                );
                                                                            })
                                                                        )}
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </CollapsibleSection>
                                        )}
                                        <div className={`flex flex-col sm:flex-row gap-2 ${!isEditMode ? "mt-2" : "mt-4"}`}>
                                            <span className="font-bold min-w-[200px]">Home Address:</span>
                                            {!isEditMode ? (
                                                <span>{userdata?.homeaddress || "-"}</span>
                                            ) : (
                                                <div className="flex flex-col gap-2">
                                                    <InputTextarea
                                                        rows={3}
                                                        value={formik.values.homeaddress}
                                                        onChange={(e) => formik.setFieldValue("homeaddress", e.target.value)}
                                                        onBlur={() => formik.setFieldTouched("homeaddress", true)}
                                                        className={`w-full sm:w-96 ${formik.touched.homeaddress && formik.errors.homeaddress
                                                            ? "p-invalid"
                                                            : ""
                                                            }`}
                                                    />
                                                    {formik.touched.homeaddress && formik.errors.homeaddress && (
                                                        <small className="p-error">{formik.errors.homeaddress}</small>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className={`flex flex-col gap-2 ${!isEditMode ? "mt-2" : "mt-4"}`}>
                                            <span className="font-bold min-w-[200px]">Bio:</span>
                                            {!isEditMode ? (
                                                <p className="m-0" style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                                                    {renderFormattedText(userdata?.bio)}
                                                </p>
                                            ) : (
                                                <div className="flex flex-col gap-2">
                                                    <InputTextarea
                                                        rows={3}
                                                        value={formik.values.bio}
                                                        onChange={(e) => formik.setFieldValue("bio", e.target.value)}
                                                        onBlur={() => formik.setFieldTouched("bio", true)}
                                                        className={`w-full sm:w-96 ${formik.touched.bio && formik.errors.bio
                                                            ? "p-invalid"
                                                            : ""
                                                            }`}
                                                    />
                                                    {formik.touched.bio && formik.errors.bio && (
                                                        <small className="p-error">{formik.errors.bio}</small>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className={`flex flex-col sm:flex-row gap-2 ${!isEditMode ? "mt-2" : "mt-4"}`}>
                                            <span className="font-bold min-w-[200px]">Mobile Number:</span>
                                            {!isEditMode ? (
                                                <span>{userdata?.mobileno}</span>
                                            ) : (
                                                <div className="flex flex-col gap-2">
                                                    <InputText
                                                        name="mobileno"
                                                        placeholder="Mobile Number"
                                                        value={formik.values.mobileno}
                                                        onChange={formik.handleChange}
                                                        className={`${formik.touched.mobileno && formik.errors.mobileno ? "p-invalid" : ""}`}
                                                    />
                                                    {formik.touched.mobileno && formik.errors.mobileno && (
                                                        <small className="p-error">{formik.errors.mobileno}</small>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className={`flex flex-col sm:flex-row gap-2 mt-2`}>
                                            <span className="font-bold min-w-[200px]">Followers:</span>
                                            <span>{userdata?.followers || 0}</span>
                                        </div>
                                        <div className={`flex flex-col sm:flex-row gap-2 mt-2`}>
                                            <span className="font-bold min-w-[200px]">Followings:</span>
                                            <span>{userdata?.followings || 0}</span>
                                        </div>
                                        <div className={`flex flex-col sm:flex-row gap-2 mt-2`}>
                                            <span className="font-bold min-w-[200px]">Referred By:</span>
                                            <span>{userdata?.approvername || "-"}</span>
                                        </div>
                                        <div className={`flex flex-col sm:flex-row gap-2 mt-2`}>
                                            <span className="font-bold min-w-[200px]">Confirmed By / Approved By:</span>
                                            <span>{userdata?.confirmedbyname || "-"}</span>
                                        </div>
                                        <div className={`flex flex-col sm:flex-row gap-2 mt-2`}>
                                            <span className="font-bold min-w-[200px]">Register Date and Time :</span>
                                            <span>{
                                                new Date(userdata?.createddate).toLocaleString("en-GB", {
                                                    day: "2-digit",
                                                    month: "2-digit",
                                                    year: "numeric",
                                                    hour: "2-digit",
                                                    minute: "2-digit",
                                                    second: "2-digit",
                                                    hour12: true,
                                                })
                                            }</span>
                                        </div>
                                        <div className="flex flex-col md:flex-row items-start md:items-center gap-6 mt-4">
                                            <div className="w-[80px] h-[80px] sm:w-[100px] sm:h-[100px]">
                                                <CircularProgressbar
                                                    value={userdata?.totalfill || 0}
                                                    text={`${userdata?.totalfill || 0}%`}
                                                    styles={buildStyles({
                                                        textColor: "#000",
                                                        pathColor: "#007ad9",
                                                        trailColor: "#d6d6d6",
                                                    })}
                                                />
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
                                                {[
                                                    ["Basic Details", userdata?.basicdetailfill],
                                                    ["Occupation Details", userdata?.occupationfill],
                                                    ["Family Details", userdata?.familyfill],
                                                    ["Education Details", userdata?.educationfill],
                                                ].map(([label, status], i) => (
                                                    <div key={i} className="flex items-center gap-2">
                                                        {status ? (
                                                            <CheckCircleIcon className="w-6 h-6 text-green-600" />
                                                        ) : (
                                                            <XCircleIcon className="w-6 h-6 text-gray-400" />
                                                        )}
                                                        <span>{label}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </form>
                                </div>
                                <div className="col-span-12 md:col-span-4 flex flex-col items-center gap-4">
                                    {[
                                        { key: "profileimg", label: "Profile Photo", url: userdata?.profileimgurl },
                                        { key: "posterimg", label: "Poster Photo", url: userdata?.posterimgurl },
                                    ].map(img => (
                                        <div key={img.key} className="relative flex flex-col items-center gap-1">
                                            <div
                                                className="rounded border overflow-hidden cursor-pointer bg-gray-50 flex items-center justify-center"
                                                style={{ width: "200px", height: "260px", borderColor: "#e5e7eb" }}
                                                onClick={() => setSelectedImage(img.url)}
                                            >
                                                <img
                                                    src={img.url}
                                                    alt={img.label}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <span className="text-xs" style={{ color: "#9ca3af" }}>{img.label}</span>
                                            {isEditMode && (
                                                <>
                                                    <button
                                                        className="absolute top-2 right-2 bg-white p-1 rounded shadow"
                                                        onClick={() => document.getElementById(img.key)?.click()}
                                                    >
                                                        <PencilIcon className="h-4 w-4" />
                                                    </button>
                                                    <input
                                                        id={img.key}
                                                        type="file"
                                                        hidden
                                                        accept="image/*"
                                                        onChange={(e) => {
                                                            const file = e.target.files?.[0];
                                                            if (file) {
                                                                handleImageUpload(file, img.key); // profileimg or posterimg
                                                            }
                                                        }}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </Card>
                <Card className="w-100 h-auto mt-2" title="Educational Info">
                    {userdata?.education && userdata.education.length > 0 ? (
                        <Accordion multiple activeIndex={null}>
                            {userdata.education.map((item: any, index: number) => (
                                <AccordionTab key={item.id} header={`${item.category} - ${item.institute}`}>
                                    <div className="p-1">
                                        <p><strong>Description:</strong> {item.description}</p>
                                        <p><strong>Start Date:</strong> {item.startdateshow}</p>
                                        <p><strong>End Date:</strong> {item.ispursing ? 'Pursuing' : item.enddateshow}</p>
                                        <p><strong>CGPA:</strong> {item.cgpa}</p>
                                        <p><strong>Duration:</strong> {item.gap}</p>
                                    </div>
                                </AccordionTab>
                            ))}
                        </Accordion>
                    ) : (
                        <p>No education info available.</p>
                    )}
                </Card>
                <Card className="w-100 h-auto mt-4" title="Occupation Info">
                    {userdata?.occupation && userdata.occupation.length > 0 ? (
                        <Accordion multiple activeIndex={null}>
                            {userdata.occupation.map((job: any) => (
                                <AccordionTab key={job.id} header={`${job.designation} - ${job.companyname}`}>
                                    <div className="p-1">
                                        <p><strong>Designation:</strong> {job.designation}</p>
                                        <p><strong>Company:</strong> {job.companyname}</p>
                                        <p><strong>Start Date:</strong> {job.startdateshow}</p>
                                        <p><strong>End Date:</strong> {job.enddateshow || "(Current)"}</p>
                                        <p><strong>Salary:</strong> {job.salary}</p>
                                        <p><strong>Duration:</strong> {job.gap}</p>
                                    </div>
                                </AccordionTab>
                            ))}
                        </Accordion>
                    ) : (
                        <p>No occupation info available.</p>
                    )}
                </Card>
                <Card
                    className="w-full h-auto mt-2"
                    title={
                        <div className="flex items-center justify-between">
                            <span>Identity Info</span>
                            {!identityEditMode ? (
                                <Button label="Edit" size="small" outlined style={{ color: "#264164", borderColor: "#264164" }} onClick={startIdentityEdit} />
                            ) : (
                                <div className="flex gap-2">
                                    <Button label="Cancel" size="small" className="p-button-text" onClick={() => setIdentityEditMode(false)} disabled={savingIdentity} />
                                    <Button label="Save" size="small" loading={savingIdentity} onClick={handleSaveIdentity} />
                                </div>
                            )}
                        </div>
                    }
                >
                    <div className="grid grid-cols-12 gap-4 mt-4">
                        <div className="col-span-12">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2">
                                <span className="font-bold whitespace-nowrap">Aadhar Card Number:</span>
                                {identityEditMode ? (
                                    <PrimeInputText
                                        value={identityAdharno}
                                        onChange={(e) => setIdentityAdharno(e.target.value)}
                                        className="w-full sm:w-64"
                                    />
                                ) : (
                                    <span className="break-all">{userdata?.adharno || "-"}</span>
                                )}
                            </div>
                            <div className="flex flex-col sm:flex-row gap-4 mt-4">
                                {[
                                    { side: "front" as const, label: "Aadhar Front", preview: identityFrontPreview, url: userdata?.identityfrontimgurl, inputId: "identityfrontimg-upload" },
                                    { side: "back" as const, label: "Aadhar Back", preview: identityBackPreview, url: userdata?.identitybackimgurl, inputId: "identitybackimg-upload" },
                                ].map((doc) => (
                                    <div key={doc.side} className="relative flex flex-col items-start gap-1">
                                        <p className="m-0 mb-1 text-xs font-medium text-gray-500">{doc.label}</p>
                                        <div
                                            className="rounded border overflow-hidden bg-gray-50 flex items-center justify-center"
                                            style={{ width: "208px", height: "130px", borderColor: "#e5e7eb" }}
                                        >
                                            {(doc.preview || doc.url) ? (
                                                <img
                                                    src={doc.preview || doc.url}
                                                    alt={doc.label}
                                                    onClick={() => !identityEditMode && setSelectedImage(doc.url)}
                                                    className={`w-full h-full object-cover ${!identityEditMode ? "cursor-pointer" : ""}`}
                                                />
                                            ) : (
                                                <span className="text-xs" style={{ color: "#d1d5db" }}>No image</span>
                                            )}
                                        </div>
                                        {identityEditMode && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="absolute p-1 rounded shadow bg-white"
                                                    style={{ top: "22px", right: "8px" }}
                                                    onClick={() => document.getElementById(doc.inputId)?.click()}
                                                >
                                                    <PencilIcon className="h-4 w-4" />
                                                </button>
                                                <input
                                                    id={doc.inputId}
                                                    type="file"
                                                    hidden
                                                    accept="image/*"
                                                    onChange={(e) => handleIdentityFileChange(e.target.files?.[0] || null, doc.side)}
                                                />
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>

                        </div>
                    </div>
                </Card>
                <CollapsibleSection title="Family Tree" onOpen={handleFamilyTreeTabOpen}>
                    {!familyTreeData ? (
                        <div className="py-4 text-center" style={{ color: "#9ca3af" }}>Loading…</div>
                    ) : (
                        <div style={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                            <TreeComponent familyTreeData={familyTreeData} />
                        </div>
                    )}
                </CollapsibleSection>
                {userdata?.followers > 0 && (<CollapsibleSection title={`Followers (${userdata.followers})`} onOpen={handleFollowersTabOpen}>
                    <DataTable
                        scrollable
                        header={follwersheader}
                        value={followersData}
                        resizableColumns
                        removableSort
                        rows={itemsFollowersPerPage}
                    >
                        <Column field="username" header="User name" />
                    </DataTable>
                    <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 bg-white p-2">
                        <Paginator
                            template={paginatoreFollowersTemplate}
                            first={startFollowersIndex}
                            rows={itemsFollowersPerPage}
                            totalRecords={totalFollowersRecords}
                        />
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                            <Button
                                onClick={() => handleFollowersPageChange(currentFollowersPage - 1)}
                                className="pagination-button flex-shrink-0 px-2 sm:px-4"
                                disabled={currentFollowersPage === 0}
                            >
                                <span className="sm:hidden">Prev</span>
                                <span className="hidden sm:inline">Previous</span>
                            </Button>

                            <Button className="pagination-button flex-1 min-w-0 cursor-default px-1 sm:px-4">
                                <span className="pagination-text whitespace-nowrap text-xs sm:text-sm">
                                    Page {currentFollowersPage + 1} of {totalFollowersPages || 1}
                                </span>
                            </Button>

                            <Button
                                onClick={() => handleFollowersPageChange(currentFollowersPage + 1)}
                                className="pagination-button flex-shrink-0 px-2 sm:px-4"
                                disabled={currentFollowersPage + 1 === (totalFollowersPages === 0 ? 1 : totalFollowersPages)}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                </CollapsibleSection>)}
                {userdata?.followings > 0 && (<CollapsibleSection title={`Following (${userdata.followings})`} onOpen={handleFollowingsTabOpen}>
                    <DataTable
                        scrollable
                        header={followingheader}
                        value={followingData}
                        resizableColumns
                        removableSort
                        rows={itemsFollowingsPerPage}
                    >
                        <Column field="username" header="User name" />
                    </DataTable>
                    <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 bg-white p-2">
                        <Paginator
                            template={paginatoreFollowingsTemplate}
                            first={startFollowingsIndex}
                            rows={itemsFollowingsPerPage}
                            totalRecords={totalFollowingRecords}
                        />
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                            <Button
                                onClick={() => handleFollowingsPageChange(currentFollowingsPage - 1)}
                                className="pagination-button flex-shrink-0 px-2 sm:px-4"
                                disabled={currentFollowingsPage === 0}
                            >
                                <span className="sm:hidden">Prev</span>
                                <span className="hidden sm:inline">Previous</span>
                            </Button>

                            <Button className="pagination-button flex-1 min-w-0 cursor-default px-1 sm:px-4">
                                <span className="pagination-text whitespace-nowrap text-xs sm:text-sm">
                                    Page {currentFollowingsPage + 1} of {totalFollowingsPages || 1}
                                </span>
                            </Button>

                            <Button
                                onClick={() => handleFollowingsPageChange(currentFollowingsPage + 1)}
                                className="pagination-button flex-shrink-0 px-2 sm:px-4"
                                disabled={currentFollowingsPage + 1 === (totalFollowingsPages === 0 ? 1 : totalFollowingsPages)}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                </CollapsibleSection>)}
                {userdata?.posts > 0 && (
                    <CollapsibleSection title="Post" onOpen={handlePostTabOpen}>
                        <div className="flex flex-col gap-3 mt-4 sm:flex-row sm:items-center sm:justify-between">

                            {/* Title */}
                            <h2 className="card-title m-0 text-lg sm:text-xl">Post</h2>

                            {/* Controls */}
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">

                                {/* Total Posts */}
                                <span className="text-sm sm:text-base whitespace-nowrap">
                                    Total Posts – {userdata?.posts}
                                </span>

                                {/* Buttons */}
                                <div className="flex sm:flex-row flex-col flex-wrap gap-2">
                                    <Button
                                        className="round-button flex justify-center"
                                        disabled={!postFirstid || postCurrentid === postFirstid || userdata?.posts === 0}
                                        onClick={() => getPostData(postFirstid)}
                                    >
                                        <ArrowLeftIcon className="w-5 h-5 mr-1" />
                                        <span>First</span>
                                    </Button>

                                    <Button
                                        className="round-button justify-center"
                                        disabled={!postPrevid || userdata?.posts === 0}
                                        onClick={() => getPostData(postPrevid)}
                                    >
                                        <ArrowLeftIcon className="w-5 h-5 mr-1" />
                                        <span>Previous</span>
                                    </Button>

                                    <Button
                                        className="round-button justify-center"
                                        disabled={!postNextid || userdata?.posts === 0}
                                        onClick={() => getPostData(postNextid)}
                                    >
                                        <span>Next</span>
                                        <ArrowRightIcon className="w-5 h-5 ml-1" />
                                    </Button>

                                    <Button
                                        className="round-button justify-center"
                                        disabled={!postLatestid || userdata?.posts === 0 || postCurrentid === postLatestid}
                                        onClick={() => getPostData(postLatestid)}
                                    >
                                        <span>Latest</span>
                                        <ArrowRightIcon className="w-5 h-5 ml-1" />
                                    </Button>
                                </div>
                            </div>

                        </div>
                        <Card className="w-full h-auto mt-4">
                            <div className="grid grid-cols-12 gap-4 mt-4">

                                {/* Post Info */}
                                <div
                                    className={`col-span-12 ${images && images.length > 0 ? "md:col-span-6" : "md:col-span-12"
                                        } space-y-2`}
                                >

                                    <div className="flex flex-wrap gap-2 items-center">
                                        <span className="font-bold">Uploaded Date and Time:</span>
                                        <span>{DateFormate.formatDateTime(postDate)}</span>
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                        <span className="font-bold">Description:</span>
                                        <span className="break-words">{postDescription || "-"}</span>
                                    </div>

                                    <div className="flex gap-2 items-center">
                                        <span className="font-bold">Likes:</span>
                                        <span>{postLike}</span>
                                    </div>

                                    <div className="flex gap-2 items-center">
                                        <span className="font-bold">Comments:</span>
                                        <span>{postComment}</span>
                                    </div>

                                </div>

                                {/* Images */}
                                {images && images.length > 0 && (
                                    <div className="col-span-12 md:col-span-6 flex justify-center">
                                        <Carousel
                                            value={images}
                                            numScroll={1}
                                            numVisible={1}
                                            itemTemplate={productTemplate}
                                            responsiveOptions={responsiveOptions}

                                            /* ✅ Hide arrows & dots when single image */
                                            showIndicators={images.length > 1}
                                            showNavigators={images.length > 1}

                                            className="w-full max-w-md"
                                        />
                                    </div>
                                )}

                            </div>
                        </Card>
                        {postLike > 0 && (<div className="mt-2">
                            <DataTable
                                scrollable
                                header={likeheader}
                                value={likeData}
                                resizableColumns
                                removableSort
                                rows={itemsLikesPerPage}
                            >
                                <Column field="username" header="User name" />
                            </DataTable>
                            <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 bg-white p-2">
                                <Paginator
                                    template={paginatoreLikesTemplate}
                                    first={startLikesIndex}
                                    rows={itemsLikesPerPage}
                                    totalRecords={totalLikesPages}
                                />
                                <div className="flex items-center gap-2 w-full sm:w-auto">
                                    <Button
                                        onClick={() => handleLikesPageChange(currentLikesPage - 1)}
                                        className="pagination-button flex-shrink-0 px-2 sm:px-4"
                                        disabled={currentLikesPage === 0}
                                    >
                                        <span className="sm:hidden">Prev</span>
                                        <span className="hidden sm:inline">Previous</span>
                                    </Button>

                                    <Button className="pagination-button flex-1 min-w-0 cursor-default px-1 sm:px-4">
                                        <span className="pagination-text whitespace-nowrap text-xs sm:text-sm">
                                            Page {currentLikesPage + 1} of {totalLikesPages || 1}
                                        </span>
                                    </Button>

                                    <Button
                                        onClick={() => handleLikesPageChange(currentLikesPage + 1)}
                                        className="pagination-button flex-shrink-0 px-2 sm:px-4"
                                        disabled={currentLikesPage + 1 === (totalLikesPages === 0 ? 1 : totalLikesPages)}
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        </div>)}
                        {postComment > 0 && (<div className="mt-2">
                            {isMobile ? (
                                <div className="bg-white w-full overflow-x-hidden box-border" style={{ borderRadius: "8px" }}>
                                    <div className="p-3 pb-2">{commentheader}</div>
                                    <div className="flex flex-col gap-2 px-3 pb-3">
                                        {commentData.map((rowData: any, idx: number) => (
                                            <div key={rowData._id || idx} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                                <p className="m-0 font-semibold text-sm break-words" style={{ color: "#264164" }}>{rowData.username}</p>
                                                <p className="m-0 text-xs break-words mt-1" style={{ color: "#4b5563" }}>{rowData.text}</p>
                                                <div className="text-xs mt-1" style={{ color: "#9ca3af" }}>{commentDateBodyTemplate(rowData)}</div>
                                                <div className="flex justify-end gap-2 mt-2">
                                                    {actionBodyTemplate(rowData)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <DataTable
                                    scrollable
                                    header={commentheader}
                                    value={commentData}
                                    resizableColumns
                                    removableSort
                                    rows={itemsCommentsPerPage}
                                >
                                    <Column field="username" header="User name" />
                                    <Column header="Text" body={textBodyTemplate} />
                                    <Column header="Comment Date and Time" body={commentDateBodyTemplate} />
                                    <Column header="Action" className="action-column" frozen body={actionBodyTemplate} />
                                </DataTable>
                            )}
                            <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 bg-white p-2">
                                <Paginator
                                    template={paginatoreCommentsTemplate}
                                    first={startCommentsIndex}
                                    rows={itemsCommentsPerPage}
                                    totalRecords={totalCommentRecords}
                                />
                                <div className="flex items-center gap-2 w-full sm:w-auto">
                                    <Button
                                        onClick={() => handleCommentsPageChange(currentCommentsPage - 1)}
                                        className="pagination-button flex-shrink-0 px-2 sm:px-4"
                                        disabled={currentCommentsPage === 0}
                                    >
                                        <span className="sm:hidden">Prev</span>
                                        <span className="hidden sm:inline">Previous</span>
                                    </Button>

                                    <Button className="pagination-button flex-1 min-w-0 cursor-default px-1 sm:px-4">
                                        <span className="pagination-text whitespace-nowrap text-xs sm:text-sm">
                                            Page {currentCommentsPage + 1} of {totalCommentsPages || 1}
                                        </span>
                                    </Button>

                                    <Button
                                        onClick={() => handleCommentsPageChange(currentCommentsPage + 1)}
                                        className="pagination-button flex-shrink-0 px-2 sm:px-4"
                                        disabled={currentCommentsPage + 1 === (totalCommentsPages === 0 ? 1 : totalCommentsPages)}
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        </div>)}
                    </CollapsibleSection>
                )}
            </div>
            {selectedImage && (
                <ImagePreviewModal selectedImage={selectedImage} onclose={handleClosePreview} />
            )}
            <Dialog
                header="Send Notification"
                visible={messageDialogOpen}
                style={{ width: "90vw", maxWidth: "500px" }}
                modal
                draggable={false}
                onHide={() => { setMessageDialogOpen(false); resetMessageDialog(); }}
            >
                <div className="flex flex-col gap-3">
                    <div className="font-semibold text-sm">To: 1 Users</div>

                    <InputTextarea
                        value={messageTitle}
                        onChange={(e) => setMessageTitle(e.target.value)}
                        rows={2}
                        placeholder="Enter notification title..."
                        className="w-full"
                    />

                    <InputTextarea
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        rows={4}
                        placeholder="Enter notification message..."
                        className="w-full"
                    />

                    <div>
                        <label className="text-sm font-medium mb-1 block">Send Type</label>
                        <Dropdown
                            value={sendType}
                            options={sendTypeOptions}
                            onChange={(e) => setSendType(e.value)}
                            optionLabel="label"
                            optionValue="value"
                            className="w-full"
                        />
                    </div>

                    {sendType === "schedule" && (
                        <>
                            <div>
                                <label className="text-sm font-medium mb-1 block">Schedule Type</label>
                                <Dropdown
                                    value={scheduleType}
                                    options={scheduleTypeOptions}
                                    onChange={(e) => setScheduleType(e.value)}
                                    optionLabel="label"
                                    optionValue="value"
                                    className="w-full"
                                />
                            </div>

                            {scheduleType === "once" && (
                                <Calendar
                                    value={scheduledDate}
                                    onChange={(e) => setScheduledDate(e.value)}
                                    showTime
                                    hourFormat="24"
                                    placeholder="Select Date & Time"
                                    className="w-full"
                                />
                            )}

                            {(scheduleType === "daily" ||
                                scheduleType === "weekly" ||
                                scheduleType === "monthly" ||
                                scheduleType === "specific_days") && (
                                    <input
                                        type="time"
                                        value={scheduledTime}
                                        onChange={(e) => setScheduledTime(e.target.value)}
                                        className="p-2 border rounded w-full"
                                    />
                                )}

                            {scheduleType === "monthly" && (
                                <PrimeInputText
                                    value={monthDate.toString()}
                                    onChange={(e: any) => {
                                        let val = parseInt(e.target.value || "1");
                                        if (isNaN(val)) val = 1;
                                        if (val < 1) val = 1;
                                        if (val > 31) val = 31;
                                        setMonthDate(val);
                                    }}
                                    placeholder="Enter month date (1-31)"
                                    className="w-full"
                                />
                            )}

                            {scheduleType === "specific_days" && (
                                <MultiSelect
                                    value={specificDays}
                                    options={weekDays.map(day => ({ label: day, value: day }))}
                                    onChange={(e) => setSpecificDays(e.value)}
                                    placeholder="Select specific days"
                                    className="w-full"
                                />
                            )}

                            {scheduleType === "day_time" && (
                                <div className="flex flex-col gap-2 border rounded p-3">
                                    <label className="text-sm font-medium">Select days and set time for each</label>
                                    {weekDays.map(day => {
                                        const entry = dayTimes.find(d => d.day === day);
                                        return (
                                            <div key={day} className="flex items-center gap-3">
                                                <input
                                                    type="checkbox"
                                                    checked={!!entry}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setDayTimes(prev => [...prev, { day, time: "09:00" }]);
                                                        } else {
                                                            setDayTimes(prev => prev.filter(d => d.day !== day));
                                                        }
                                                    }}
                                                />
                                                <span className="capitalize text-sm" style={{ width: "90px" }}>{day}</span>
                                                {entry && (
                                                    <input
                                                        type="time"
                                                        value={entry.time}
                                                        onChange={(e) =>
                                                            setDayTimes(prev => prev.map(d =>
                                                                d.day === day ? { ...d, time: e.target.value } : d
                                                            ))
                                                        }
                                                        className="p-1 border rounded"
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}

                    <WhatsAppTemplateSection
                        template={waTemplate}
                        params={waParams}
                        onTemplateChange={setWaTemplate}
                        onParamsChange={setWaParams}
                    />

                    <div className="flex justify-end gap-2 mt-3 flex-wrap">
                        <Button
                            label="Cancel"
                            severity="secondary"
                            onClick={() => { setMessageDialogOpen(false); resetMessageDialog(); }}
                            disabled={sendingMessage || sendingWhatsApp}
                        />

                        <Button
                            label="Send WhatsApp"
                            icon={<WhatsAppIcon size={16} />}
                            severity="success"
                            loading={sendingWhatsApp}
                            disabled={!waTemplate || sendingMessage}
                            onClick={handleSendWhatsApp}
                        />

                        <Button
                            label={sendType === "instant" ? "Send Now" : "Schedule"}
                            icon="pi pi-send"
                            loading={sendingMessage}
                            disabled={sendingWhatsApp || !messageTitle.trim() || !messageText.trim()}
                            onClick={handleSendMessage}
                        />
                    </div>
                </div>
            </Dialog>
            <Dialog
                header="Reject User"
                visible={rejectDialogOpen}
                style={{ width: "30rem" }}
                draggable={false}
                onHide={() => { setRejectDialogOpen(false); setRejectReason(""); }}
            >
                <label className="text-sm font-medium text-gray-700">
                    Reason (sent to the user as a notification + SMS)
                </label>
                <InputTextarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    rows={4}
                    className="w-full mt-2"
                    placeholder="e.g. ID proof image is unreadable, please re-upload."
                    autoFocus
                />
                <div className="flex justify-end gap-2 mt-4">
                    <Button
                        label="Cancel"
                        className="p-button-text"
                        onClick={() => { setRejectDialogOpen(false); setRejectReason(""); }}
                    />
                    <Button
                        label="Reject"
                        style={{ color: "#fff", background: "#FF0000" }}
                        onClick={handleReject}
                        disabled={!rejectReason.trim()}
                    />
                </div>
            </Dialog>
        </>
    );
};

export default ProfileView;
